import { Fragment, type JSX, useCallback, useEffect, useRef, useState } from 'react';

import type { Kopf } from '../komponenten/Rahmen';
import { Aktenrumpf, Kennzahlenleiste, Reiterleiste } from '../komponenten/Akte';
import { Aktenverweis, Verbunden, type Aktenwege } from '../komponenten/Aktenwege';
import { KundeVerweis } from '../komponenten/Kontaktkarte';
import { STATUSMARKE } from './Auftragsliste';

import {
  ABWEICHUNGSNAME,
  ANZAHLUNGSNAME,
  ERBRINGUNGSNAME,
  FAKTURIERUNGSNAME,
  HERKUNFTSNAME,
  POSITIONSARTNAME,
  STATUSNAME,
  WIRKUNGSNAME,
  ZEITPUNKTNAME,
  abweichungErfassen,
  abweichungen as holeAbweichungen,
  achsenSetzen,
  akte as holeAkte,
  anzahlungAnlegen,
  anzahlungEingang,
  anzahlungLoeschen,
  anzahlungen as holeAnzahlungen,
  feldSichern,
  loeschen,
  neueVersion,
  neuOrdnen,
  positionAnlegen,
  positionLoeschen,
  positionTerminSetzen,
  historie as holeHistorie,
  positionStornieren,
  positionStornoZuruecknehmen,
  positionStornovorschau as holePositionsvorschau,
  positionsteilnehmer as holePositionsteilnehmer,
  statusSetzen,
  stornoZuruecknehmen,
  stornovorgaenge as holeStornovorgaenge,
  verbunden as holeVerbunden,
  type Auftragsverbindungen,
  stornovorschau as holeStornovorschau,
  vorgangZuruecknehmen,
  BEZUGSNAME,
  type Bezugsart,
  type Abweichung,
  type Abweichungsart,
  type Abweichungswirkung,
  type Anzahlung,
  type Anzahlungsart,
  type Auftrag,
  type Auftragsposition,
  type Auftragsvorgang,
  type Erbringung,
  type Fakturierung,
  type Historienzeile,
  type Platzauswahl,
  type Positionsart,
  type Positionsteilnehmer,
  type Stornoposten,
  type Stornovorschau,
  VERTRAGSARTNAME,
} from '../api/auftraege';
import { VERTRIEBSFORMNAME, type Artikelzeile, type Vertriebsform } from '../api/produkte';
import { Artikelwahl } from '../produkt/Artikelwahl';
import { exklusive, type Exklusivtermin } from '../api/termine';
import { terminbezug, terminname } from '../api/terminname';
import { Belegabschnitt } from '../beleg/Belegabschnitt';
import { BgGutscheine, Gutscheinvormerkungen } from '../rechnung/BgZuschuss';
import {
  ausAuftraegen as rechnungAusAuftraegen,
  ausAuftragsstorno as rechnungAusAuftragsstorno,
  BELEGARTNAME,
  type Rechnungsart,
} from '../api/rechnungen';
import { ApiFehler } from '../api/fehler';
import { alsEuro, alsMenge, alsMengenangabe, alsProzent } from '../betrag';
import { alsTag, alsZeitpunkt, heute } from '../datum';
import { markenklasse } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import { AbTabletHinweis } from '../komponenten/AbTablet';
import { useTelefon } from '../komponenten/telefon';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { EntwurfLoeschen } from '../komponenten/EntwurfLoeschen';
import { herabsetzen } from '../api/buchungen';
import {
  ERKLAERUNGSWEGNAME,
  Erklaerungsfelder,
  Platzauswahl as Platzauswahlfelder,
  Stornokorrektur,
  auswahlStimmt,
  erklaerungsrumpf,
  neueErklaerung,
  type Erklaerungsangaben,
} from '../komponenten/Stornoangaben';
import { Rundungshinweis } from '../komponenten/Rundungshinweis';
import { Telefonfenster, Zeilenformular } from '../komponenten/Zeilenformular';
import { Dialog } from '../komponenten/Dialog';
import { Platzterminwahl } from './Platzterminwahl';

type Reiter = 'positionen' | 'kopf' | 'beleg' | 'anzahlungen' | 'abweichungen' | 'historie';

const REITER: readonly { kennung: Reiter; beschriftung: string }[] = [
  { kennung: 'positionen', beschriftung: 'Positionen' },
  { kennung: 'kopf', beschriftung: 'Kopfdaten' },
  { kennung: 'beleg', beschriftung: 'Beleg' },
  { kennung: 'anzahlungen', beschriftung: 'Anzahlungen' },
  { kennung: 'abweichungen', beschriftung: 'Abweichungen' },
  { kennung: 'historie', beschriftung: 'Historie' },
  /*
   * STATUS, SUMMEN UND FASSUNGEN STEHEN IN DER SEITENLEISTE (ÄA-41, Auftrag
   * #18) — wie in der Angebotsakte. Die Historie bleibt ein Reiter: Sie ist
   * eine breite Tabelle, keine Zeitleiste.
   */
];

const ARTNAME: Readonly<Record<Positionsart, string>> = {
  artikel: 'Artikel',
  freitext: 'Freitext',
  text: 'Textzeile',
  zwischensumme: 'Zwischensumme',
  rabatt: 'Rabattzeile',
};

/**
 * Die Auftragsakte (Lastenheft Kapitel 7).
 *
 * SIE ZEIGT DREI ZUSTÄNDE UND NICHT EINEN. AUF-12 zählt sieben Zustände auf
 * und sagt im nächsten Satz, dass „Erbringung und Berechnung zwei getrennte
 * Achsen" sind. Die Statusleiste zeigt sie deshalb nebeneinander: der
 * Lebenslauf links, die beiden Achsen rechts.
 *
 * DIE BEIDEN ACHSEN SIND NICHT ANKLICKBAR. Sie werden aus den Positionen
 * fortgeschrieben (AUF-13) — ein Auswahlfeld dafür wäre die Einladung, sie zu
 * setzen, statt sie zu erarbeiten. Die Übersteuerung gibt es trotzdem; sie
 * steht als eigener Kasten hinter einem eigenen Recht und verlangt einen
 * Grund.
 */
export function Auftragsakte({
  id,
  darfAendern,
  darfBestaetigen,
  darfUebersteuern,
  beiKopf,
  beiListe,
  beiVersion,
  beiAngebot,
  darfFakturieren,
  darfBuchen = false,
  darfHerabsetzen = false,
  darfLoeschen = false,
  bgGutscheine,
  beiRechnung,
  wege,
}: {
  readonly id: string;
  readonly darfAendern: boolean;
  readonly darfBestaetigen: boolean;
  /** `auftrag/loeschen` (0033): den Entwurf logisch löschen (DAT-25) — Auftrag CRM-119. */
  readonly darfLoeschen?: boolean;
  readonly darfUebersteuern: boolean;
  /** CRM-118 (AUF-06, BUC-50): die Wege zu den verbundenen Akten, nach Recht gesetzt. */
  readonly wege?: Aktenwege;
  readonly beiKopf: (kopf: Kopf) => void;
  readonly beiListe: () => void;
  readonly beiVersion: (id: string) => void;
  readonly beiAngebot: (id: string) => void;
  /** AUF-19: der Weg zur Rechnung. */
  readonly darfFakturieren: boolean;
  /** C7.1: den Eingang einer Anzahlung vermerken — rechnung/buchen, wie die API. */
  readonly darfBuchen?: boolean;
  /** BUC-146 (AeA-59): die Stornogebühr senken — buchung/herabsetzen, wie die API. */
  readonly darfHerabsetzen?: boolean;
  /** PRD-38 (CRM-157): Gutscheine von Kostenträgern sehen (bgzuschuss/sehen) und erfassen (bgzuschuss/erfassen). */
  readonly bgGutscheine?: { readonly darfErfassen: boolean };
  readonly beiRechnung: (id: string) => void;
}): JSX.Element {
  const [auftrag, setzeAuftrag] = useState<Auftrag | undefined>(undefined);
  const telefon = useTelefon();
  /** BUC-145: die Stornovorgänge; `undefined`, wenn sie nicht zu laden waren. */
  const [vorgaenge, setzeVorgaenge] = useState<readonly Auftragsvorgang[] | undefined>(undefined);
  const [reiter, setzeReiter] = useState<Reiter>('positionen');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [rueckmeldung, setzeRueckmeldung] = useState('');
  /** BUC-145: der erzeugte Stornobeleg samt dem, was von Hand fehlt. */
  const [belegmeldung, setzeBelegmeldung] = useState<
    { readonly id: string; readonly hinweise: readonly string[] } | undefined
  >(undefined);
  // Wie in der Angebotsakte: Die Meldung steht am Seitenanfang, die Handlung
  // weit darunter — der Verweis holt den Blick dorthin (ERR-01).
  const meldungsverweis = useRef<HTMLDivElement>(null);
  /* Im Ref: `laden` setzt den Kopf und soll nicht bei jedem Bild neu entstehen. */
  const zurListe = useRef(beiListe);
  zurListe.current = beiListe;
  const wegeImKopf = useRef(wege);
  wegeImKopf.current = wege;
  /** CRM-118: Rechnungen, Buchungen, Termine und Rahmen; `undefined`, solange nicht geladen. */
  const [verbindungen, setzeVerbindungen] = useState<Auftragsverbindungen | undefined>(undefined);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const [geladen, geladeneVorgaenge, geladeneVerbindungen] = await Promise.all([
        holeAkte(id),
        // Ohne die Liste bleibt die Akte bedienbar; nur Beleg und Rücknahme wissen weniger.
        holeStornovorgaenge(id).catch(() => undefined),
        // Ebenso ohne die Verbindungen: Dann fehlt nur die Karte „Verbunden".
        holeVerbunden(id).catch(() => undefined),
      ]);
      setzeAuftrag(geladen);
      setzeVorgaenge(geladeneVorgaenge);
      setzeVerbindungen(geladeneVerbindungen);
      // Kicker ordnet ein, Titel nennt den Betreff, Unterzeile den Kunden
      // (2.10, Skizzen zum Belegmuster).
      beiKopf({
        kicker: `Auftrag · ${geladen.auftragsnummer}`,
        titel: geladen.betreff,
        unterzeile: geladen.kundenname,
        sinnbild: 'clipboard-text',
        kennzeichen: (
          <>
            <span className={markenklasse(STATUSMARKE[geladen.status])}>
              {STATUSNAME[geladen.status]}
            </span>
            {geladen.version > 1 && <span className="marke">Fassung {geladen.version}</span>}
            {geladen.eingefroren && <span className="marke marke--gesperrt">eingefroren</span>}
          </>
        ),
        /*
         * Die beiden Achsen nach AUF-12 stehen in der Statusleiste daneben —
         * hier die Angaben, nach denen man den Auftrag wiederfindet.
         */
        kennzahlen: (
          <Kennzahlenleiste
            zahlen={[
              {
                beschriftung: 'Kunde',
                wert: (
                  <Aktenverweis ziel="kunde" id={geladen.kunde} wege={wegeImKopf.current}>
                    {geladen.kundenname}
                  </Aktenverweis>
                ),
              },
              { beschriftung: 'Belegdatum', wert: alsTag(geladen.belegdatum) ?? '—' },
              {
                beschriftung: 'Aus Angebot',
                wert:
                  geladen.angebotsnummer === null ? (
                    '—'
                  ) : (
                    <Aktenverweis ziel="angebot" id={geladen.angebot} wege={wegeImKopf.current}>
                      {geladen.angebotsnummer}
                    </Aktenverweis>
                  ),
              },
              { beschriftung: 'Bestellreferenz', wert: geladen.bestellreferenz ?? '—' },
              { beschriftung: 'Brutto', wert: alsEuro(geladen.summen.belegbrutto) },
              { beschriftung: 'Bearbeitung', wert: geladen.bearbeiterName ?? '—' },
            ]}
          />
        ),
        handlungen: (
          <button type="button" className="knopf" onClick={() => zurListe.current()}>
            Zur Liste
          </button>
        ),
      });
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [id, beiKopf]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function handle(was: () => Promise<unknown>, meldung: string): Promise<void> {
    await handleMit(async () => {
      await was();
      return meldung;
    });
  }

  /**
   * Wie `handle`, nur bestimmt der Vorgang SELBST, was er meldet.
   *
   * Gebraucht, seit eine Position die Fassung wechseln kann (AUF-14): „Position
   * hinzugefügt." waere dann die halbe Wahrheit — der Anwender steht danach auf
   * einer anderen Fassung, und das muss dastehen.
   */
  async function handleMit(was: () => Promise<string>): Promise<void> {
    try {
      await wirkeMit(was);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
      window.setTimeout(() => meldungsverweis.current?.focus(), 0);
    }
  }

  /**
   * Der Kern von `handleMit` — ohne den Fang. Hinter einer Rückfrage
   * (CRM-103) bleibt der Fehler im Pop-up stehen, nicht über der Maske.
   */
  async function wirkeMit(was: () => Promise<string>): Promise<void> {
    const meldung = await was();
    setzeRueckmeldung(meldung);
    setzeFehler(undefined);
    await laden();
  }

  async function wirke(was: () => Promise<unknown>, meldung: string): Promise<void> {
    await wirkeMit(async () => {
      await was();
      return meldung;
    });
  }

  if (auftrag === undefined) {
    return (
      <>
        {/* Nach einem Fehler lädt nichts mehr — dann steht nur die Meldung da. */}
        {fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>}
      </>
    );
  }

  /*
   * AUF-14: BEARBEITBAR IST NUR DIE FASSUNG, DIE DER KUNDE NOCH NICHT HAT.
   *
   * Zwei Gruende sperren, und sie bedeuten Verschiedenes:
   *   eingefroren      — es gibt eine neuere Fassung; hier ist nichts mehr zu
   *                      holen, die Arbeit geht dort weiter.
   *   festgeschrieben  — der Kunde hat DIESE Fassung. Die Arbeit geht hier
   *                      weiter, aber in einer neuen Fassung.
   */
  const bearbeitbar = darfAendern && !auftrag.eingefroren && !auftrag.festgeschrieben;

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} verweis={meldungsverweis} />}
      {rueckmeldung !== '' && (
        <p className="hinweiszeile" role="status">
          {rueckmeldung}
        </p>
      )}

      {belegmeldung !== undefined && (
        <div className="karte" role="status">
          <p>
            <span className="marke marke--gut">Beleg angelegt</span> Er steht als Entwurf in der
            Faktura; die Nummer entsteht mit der Festschreibung.
          </p>
          {/* Was nicht darauf steht, steht hier — und nicht im Protokoll (BUC-145). */}
          {belegmeldung.hinweise.map((h) => (
            <p key={h} className="feld__fehler-satz">
              {h}
            </p>
          ))}
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => setzeBelegmeldung(undefined)}
            >
              Schließen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => beiRechnung(belegmeldung.id)}
            >
              Beleg öffnen
            </button>
          </div>
        </div>
      )}

      {auftrag.eingefroren && (
        <p className="warnung">
          Diese Fassung ist abgelöst und bleibt unverändert einsehbar. Änderungen gehören in die
          aktuelle Fassung — sie steht unter „Fassungen“ in der Seitenleiste.
        </p>
      )}

      {!auftrag.eingefroren && auftrag.festgeschrieben && (
        <p className="warnung">
          <strong>Diese Fassung ist beim Kunden</strong> und wird deshalb nicht mehr geändert.
          Was vereinbart wurde — Positionen, Preise, Konditionen — steht fest. Erbringung,
          Berechnung und Storno laufen weiter.
          {darfAendern && (
            <>
              {' '}
              Für eine Änderung legen Sie eine <strong>neue Fassung</strong> an; die jetzige
              bleibt unverändert einsehbar, und zu einer preiswirksamen Änderung bekommt der
              Kunde eine neue Auftragsbestätigung.
            </>
          )}
        </p>
      )}

      <Aktenrumpf
        haupt={
          <>
            <Reiterleiste
              beschriftung="Auftrag"
              gewaehlt={reiter}
              beiWahl={setzeReiter}
              reiter={REITER.map((r) => ({
                ...r,
                ...(r.kennung === 'positionen' ? { zahl: auftrag.positionen.length } : {}),
              }))}
            />
            <div role="tabpanel" id={`tafel-${reiter}`} aria-labelledby={`reiter-${reiter}`}>
              {/*
                * Positionen anlegen, löschen und ordnen ab Tablet (Auftrag CRM-106,
                * ÄA-63). Der Storno einer Position bleibt auf dem Telefon ein
                * Handgriff und öffnet dort als Dialog.
                */}
              {reiter === 'positionen' && <AbTabletHinweis an={bearbeitbar} />}
              {reiter === 'positionen' && (
                <Positionen
                  auftrag={auftrag}
                  bearbeitbar={bearbeitbar && !telefon}
                  /*
                   * AUF-14: DIE FASSUNG KANN SICH DABEI AENDERN.
                   *
                   * Eine Fassung, die aus einem Angebot entstanden ist, wird nicht
                   * ueberschrieben — der Server friert sie ein und legt die Zeile in
                   * einer neuen an (ANG-18: der Auftrag IST das angenommene Angebot).
                   * Dann traegt die Antwort `auftrag`, und die Maske wechselt dorthin;
                   * ohne den Wechsel zeigte sie eine Fassung, in der die eben
                   * angelegte Zeile nicht steht.
                   */
                  beiAnlegen={(eingabe) =>
                    void handleMit(async () => {
                      const antwort = await positionAnlegen(auftrag.id, eingabe);
                      if (antwort.auftrag === undefined) return 'Position hinzugefügt.';
                      beiVersion(antwort.auftrag.id);
                      return `Position hinzugefügt. Fassung ${String(antwort.auftrag.version)} `
                        + 'angelegt — die Fassung aus dem Angebot bleibt unverändert einsehbar.';
                    })
                  }
                  beiLoeschen={(position) =>
                    wirke(() => positionLoeschen(auftrag.id, position), 'Position entfernt.')
                  }
                  beiTerminwahl={(position, eingabe) =>
                    wirke(
                      () => positionTerminSetzen(auftrag.id, position, eingabe),
                      eingabe.termin === null ? 'Termin abgenommen.' : 'Termin gesetzt.',
                    )
                  }
                  darfStornieren={darfBestaetigen && !auftrag.eingefroren}
                  vorgaenge={vorgaenge}
                  beiStornieren={(position, eingabe) =>
                    wirke(
                      () =>
                        positionStornieren(
                          auftrag.id,
                          position,
                          eingabe.grund,
                          eingabe.erklaerung?.zugang,
                          eingabe.erklaerung === null ? {} : erklaerungsrumpf(eingabe.erklaerung),
                          eingabe.teil,
                        ),
                      stornomeldung(
                        eingabe.teil === undefined
                          ? 'Position storniert'
                          : `${String(eingabe.teil.plaetze)} ${eingabe.teil.plaetze === 1 ? 'Platz' : 'Plätze'} storniert`,
                        eingabe.vorschau,
                      ),
                    )
                  }
                  beiRuecknahme={(position, grund) =>
                    wirke(
                      () => positionStornoZuruecknehmen(auftrag.id, position, grund),
                      'Storno der Position zurückgenommen.',
                    )
                  }
                  beiOrdnen={(reihenfolge) =>
                    void handle(() => neuOrdnen(auftrag.id, reihenfolge), 'Reihenfolge geändert.')
                  }
                />
              )}

              {reiter === 'positionen' && vorgaenge !== undefined && vorgaenge.length > 0 && (
                <Stornovorgaenge
                  auftrag={auftrag}
                  vorgaenge={vorgaenge}
                  darfHerabsetzen={darfHerabsetzen}
                  darfZuruecknehmen={darfBestaetigen && !auftrag.eingefroren}
                  beiHerabsetzen={(vorgang, betrag, grund) =>
                    wirke(
                      () => herabsetzen(vorgang, betrag, grund),
                      `Gebühr auf ${alsEuro(betrag)} herabgesetzt.`,
                    )
                  }
                  beiRuecknahme={(vorgang, grund) =>
                    wirke(
                      () => vorgangZuruecknehmen(auftrag.id, vorgang, grund),
                      'Storno zurückgenommen; die Plätze sind wieder belegt.',
                    )
                  }
                />
              )}

              {reiter === 'positionen' && bgGutscheine !== undefined && (
                <BgGutscheine
                  bezug={{
                    art: 'auftrag',
                    id: auftrag.id,
                    positionen: auftrag.positionen
                      .filter((p) => p.einzelpreis !== null)
                      .map((p) => ({ id: p.id, text: `${p.reihenfolge}. ${p.bezeichnung}` })),
                  }}
                  darfErfassen={bgGutscheine.darfErfassen}
                />
              )}

              {reiter === 'positionen' && bgGutscheine !== undefined && (
                <Gutscheinvormerkungen
                  bezug={{
                    art: 'auftrag',
                    id: auftrag.id,
                    positionen: auftrag.positionen
                      .filter((p) => p.einzelpreis !== null)
                      .map((p) => ({ id: p.id, text: `${p.reihenfolge}. ${p.bezeichnung}` })),
                  }}
                  // AeA-71: Vormerkungen sind Verlauf, kein Vertrag — auch an der bestätigten Fassung.
                  darfVormerken={bgGutscheine.darfErfassen && !auftrag.eingefroren && auftrag.status !== 'storniert'}
                />
              )}

              {reiter === 'kopf' && (
                <Kopfdaten
                  auftrag={auftrag}
                  zurKunde={wege?.kunde}
                  bearbeitbar={bearbeitbar}
                  beiSichern={(feld, wert) =>
                    void handle(() => feldSichern(auftrag.id, feld, wert, auftrag.stand), 'Gesichert.')
                  }
                />
              )}

              {reiter === 'beleg' && darfFakturieren && auftrag.status === 'bestaetigt'
                && auftrag.fakturierung !== 'berechnet' && (
                <div className="karte">
                  <div className="karte__titel">
                    <h2>Rechnung</h2>
                    {/* AUF-19 */}
                  </div>
                  <p className="feld__hilfe">
                    Aus diesem Auftrag entsteht eine Rechnung über die noch nicht berechneten Positionen.
                    Anzahlungen werden dabei abgesetzt.
                  </p>
                  {/* RNG-03, RNG-04, RNG-09 */}
                  <p className="feld__hilfe">
                    Es entsteht ein Entwurf ohne Nummer, der sich noch verwerfen lässt; unumkehrbar
                    ist erst das Festschreiben.
                  </p>
                  <div className="knopfreihe">
                    {/*
                      ÄA-62 (B): keine Rückfrage — der Entwurf ist verwerfbar und wirkt nicht
                      nach außen. NFA-21: M-61 zählt höchstens 5 Schritte (Tabelle 29).
                    */}
                    <button
                      type="button"
                      className="knopf knopf--haupt"
                      onClick={() =>
                        void handle(async () => {
                          const neu = await rechnungAusAuftraegen({
                            auftraege: [auftrag.id],
                            /*
                             * N5.1: der Tag der Rechnung, nicht der des Auftrags. Der
                             * Server setzt ihn bei der Festschreibung ohnehin neu
                             * (0256); der Entwurf soll bis dahin nichts anderes zeigen.
                             */
                            belegdatum: heute(),
                            anzahlungenAbsetzen: true,
                          });
                          beiRechnung(neu.id);
                        }, 'Rechnungsentwurf angelegt.')
                      }
                    >
                      Rechnung erzeugen
                    </button>
                  </div>
                </div>
              )}

              {reiter === 'beleg' && (
                <Belegabschnitt
                  belegart="auftrag"
                  beleg={auftrag.id}
                  /*
                    AUF-08: Die Auftragsbestaetigung ist das Dokument, das den Vertrag
                    bestaetigt. Sie haengt an `auftrag/bestaetigen` — wer Tippfehler
                    berichtigen darf, bestaetigt damit noch keinen Vertrag.
                  */
                  darfErzeugen={darfBestaetigen}
                />
              )}

              {reiter === 'anzahlungen' && (
                <Anzahlungen
                  auftrag={auftrag}
                  bearbeitbar={bearbeitbar}
                  beiAnlegen={(eingabe) =>
                    void handle(() => anzahlungAnlegen(auftrag.id, eingabe), 'Anzahlung hinterlegt.')
                  }
                  beiLoeschen={(anzahlung) =>
                    wirke(() => anzahlungLoeschen(auftrag.id, anzahlung), 'Anzahlung entfernt.')
                  }
                  darfEingang={darfBuchen}
                  beiEingang={(anzahlung, am) =>
                    wirke(async () => {
                      await anzahlungEingang(auftrag.id, anzahlung, am);
                    }, 'Eingang vermerkt.')
                  }
                  beiBerichtigen={(anzahlung, am) =>
                    void handle(async () => {
                      await anzahlungEingang(auftrag.id, anzahlung, am);
                    }, 'Eingang vermerkt.')
                  }
                />
              )}

              {reiter === 'abweichungen' && (
                <Abweichungen
                  auftrag={auftrag}
                  bearbeitbar={bearbeitbar}
                  beiErfassen={(eingabe) =>
                    void handle(() => abweichungErfassen(auftrag.id, eingabe), 'Abweichung erfasst.')
                  }
                />
              )}

              {reiter === 'historie' && <Historie auftrag={auftrag} />}
            </div>
          </>
        }
        seite={
          <>
            <div className="akte__seitentitel">
              <span>Status und Summen</span>
            </div>
            <Statusleiste
              auftrag={auftrag}
              darfBestaetigen={darfBestaetigen}
              darfAendern={darfAendern}
              darfUebersteuern={darfUebersteuern}
              vorgaenge={vorgaenge}
              beiBestaetigen={() =>
                wirke(
                  () => statusSetzen(auftrag.id, 'bestaetigt', null),
                  `Status auf „${STATUSNAME.bestaetigt}“ gesetzt.`,
                )
              }
              beiStornieren={(grund, erklaerung, vorschau) =>
                wirke(
                  () =>
                    erklaerung === null
                      ? statusSetzen(auftrag.id, 'storniert', grund)
                      : statusSetzen(
                          auftrag.id,
                          'storniert',
                          grund,
                          erklaerung.zugang,
                          erklaerungsrumpf(erklaerung),
                        ),
                  erklaerung === null
                    ? 'Entwurf verworfen.'
                    : stornomeldung('Auftrag storniert', vorschau ?? undefined),
                )
              }
              beiRuecknahme={(grund) =>
                wirke(
                  () => stornoZuruecknehmen(auftrag.id, grund),
                  'Storno zurückgenommen; der Auftrag ist wieder bestätigt.',
                )
              }
              beiRechnung={beiRechnung}
              beiAchsen={(werte) =>
                void handle(
                  () => achsenSetzen(auftrag.id, werte),
                  werte.grund === null
                    ? 'Übersteuerung aufgehoben; die Achsen werden wieder fortgeschrieben.'
                    : 'Achsen übersteuert.',
                )
              }
              beiNeueVersion={() =>
                void handle(async () => {
                  const neu = await neueVersion(auftrag.id);
                  beiVersion(neu.id);
                }, 'Neue Fassung angelegt.')
              }
              beiAngebot={beiAngebot}
              darfFakturieren={darfFakturieren}
              /*
               * BUC-145: EIN Beleg fuer alle Stornovorgaenge des Auftrags.
               *
               * OHNE SPRUNG ZUM BELEG. Die `hinweise` nennen, was NICHT auf dem
               * Beleg steht und vor der Festschreibung von Hand zu ergaenzen ist —
               * ein Wechsel der Maske wuerde sie wegwischen. Derselbe Weg wie beim
               * Gebuehrenbeleg der Buchung; der Knopf zum Beleg steht daneben.
               */
              beiStornobeleg={() =>
                wirke(async () => {
                  const neu = await rechnungAusAuftragsstorno(auftrag.id);
                  setzeBelegmeldung(neu);
                }, 'Stornobeleg als Entwurf angelegt.')
              }
            />
            {/*
              * DAT-25, AUF-14: nur die erste Fassung, die nie bestätigt war —
              * als Entwurf oder schon verworfen. Ein bestätigter Auftrag ist
              * Vertrag und endet mit dem Storno (AUF-15).
              */}
            {darfLoeschen
              && (auftrag.status === 'entwurf'
                || (auftrag.status === 'storniert' && auftrag.bestaetigtAm === null))
              && auftrag.version === 1 && !auftrag.eingefroren && (
              <EntwurfLoeschen
                frage="Auftragsentwurf löschen?"
                unterzeile={`${auftrag.auftragsnummer} · ${auftrag.kundenname}`}
                beiLoeschen={async () => {
                  await loeschen(auftrag.id);
                  beiListe();
                }}
              >
                {/* SYS-16, BUC-76: Papierkorb; Optionen werden frei, Buchungen nicht. */}
                <p>
                  Der Auftrag {auftrag.auftragsnummer} verschwindet aus der Liste und liegt 30 Tage
                  im Papierkorb. Optionen auf Termine gibt es sofort frei.
                </p>
                {auftrag.status === 'entwurf' && (
                  <p>
                    Hält der Entwurf schon gebuchte Plätze, verwerfen Sie ihn zuerst mit
                    „Stornieren“ — das gibt sie ohne Gebühr frei.
                  </p>
                )}
              </EntwurfLoeschen>
            )}
            <Summen auftrag={auftrag} />
            {verbindungen !== undefined && (
              <Auftragsverbunden auftrag={auftrag} verbindungen={verbindungen} wege={wege} />
            )}
              <div className="karte">
                <div className="karte__titel">
                  <h2>Fassungen</h2>
                  {/* AUF-14 */}
                </div>
                <div className="tabellenhuelle tabellenhuelle--telefon">
                  <table className="liste__tabelle">
                    <thead>
                      <tr>
                        <th scope="col">Fassung</th>
                        <th scope="col">Status</th>
                        <th scope="col">angelegt</th>
                        <th scope="col">Handlung</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auftrag.versionen.map((v) => (
                        <tr key={v.id}>
                          <td>
                            {v.version}
                            {v.id === auftrag.id && <span className="marke">hier</span>}
                          </td>
                          <td>
                            {STATUSNAME[v.status]}
                            {v.eingefroren && <span className="marke marke--gesperrt">eingefroren</span>}
                          </td>
                          <td>{alsTag(v.angelegtAm)}</td>
                          <td>
                            {v.id !== auftrag.id && (
                              <button
                                type="button"
                                className="knopf knopf--klein"
                                onClick={() => beiVersion(v.id)}
                              >
                                Öffnen
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="feld__hilfe">
                  Jede Änderung am bestätigten Auftrag erzeugt eine neue Fassung; die vorherige bleibt
                  unverändert einsehbar. Was bereits erbracht oder berechnet ist, wandert mit
                  — sonst wäre eine gestellte Rechnung nach jeder Änderung ein zweites Mal fällig.
                </p>
              </div>
          </>
        }
      />
    </>
  );
}

/**
 * BUC-45: Was der Storno gekostet hat, gehört in die Rückmeldung — sonst muss
 * der Anwender die Zahl aus der Tafel abschreiben, bevor sie verschwindet.
 */
function stornomeldung(
  was: string,
  vorschau: { readonly gebuehr: string; readonly zuBerechnen: string; readonly zuErstatten: string } | undefined,
): string {
  if (vorschau === undefined) return `${was}.`;
  return `${was} — Gebühr ${alsEuro(vorschau.gebuehr)}, zu zahlen ${alsEuro(vorschau.zuBerechnen)}, `
    + `zu erstatten ${alsEuro(vorschau.zuErstatten)}.`;
}

/**
 * Die drei Achsen (AUF-12) und was sich von hier aus tun lässt.
 *
 * Der Statuswechsel kommt aus `moeglicheStatus` — der Server liest ihn aus
 * der Kantenliste (AUF-12), und die Oberfläche baut ihn nicht nach. Ein
 * zweiter Graph in TypeScript liefe beim ersten neuen Übergang auseinander.
 */
function Statusleiste({
  auftrag,
  vorgaenge,
  darfBestaetigen,
  darfAendern,
  darfUebersteuern,
  beiBestaetigen,
  beiStornieren,
  beiRuecknahme,
  beiAchsen,
  beiNeueVersion,
  beiAngebot,
  darfFakturieren,
  beiStornobeleg,
  beiRechnung,
}: {
  readonly auftrag: Auftrag;
  /** Die Stornovorgänge des Auftrags; `undefined`, solange sie nicht geladen sind. */
  readonly vorgaenge: readonly Auftragsvorgang[] | undefined;
  readonly darfBestaetigen: boolean;
  readonly darfAendern: boolean;
  readonly darfUebersteuern: boolean;
  readonly beiBestaetigen: () => Promise<void>;
  /** Ohne `erklaerung`: der Entwurf wird verworfen, ohne Gebühr und ohne Zugang. */
  readonly beiStornieren: (
    grund: string,
    erklaerung: Erklaerungsangaben | null,
    vorschau: Stornovorschau | null,
  ) => Promise<void>;
  readonly beiRuecknahme: (grund: string) => Promise<void>;
  readonly beiAchsen: (werte: {
    erbringung: Erbringung;
    fakturierung: Fakturierung;
    grund: string | null;
  }) => void;
  readonly beiNeueVersion: () => void;
  readonly beiAngebot: (id: string) => void;
  readonly darfFakturieren: boolean;
  readonly beiStornobeleg: () => Promise<void>;
  readonly beiRechnung: (id: string) => void;
}): JSX.Element {
  /* CRM-103: was die offene Rückfrage auslöst, samt den Angaben, die sie nennt. */
  const [frage, setzeFrage] = useState<Statusfrage | undefined>(undefined);
  const [stornoOffen, setzeStornoOffen] = useState(false);
  const [ruecknahmeOffen, setzeRuecknahmeOffen] = useState(false);
  const [stornogrund, setzeStornogrund] = useState('');
  const [vorschau, setzeVorschau] = useState<Stornovorschau | undefined>(undefined);
  /* C7.6, BUC-144: Die Stufe folgt dem Zugang der Erklärung, nicht der Erfassung. */
  const [erklaerung, setzeErklaerung] = useState<Erklaerungsangaben>(neueErklaerung);
  const [vorschaufehler, setzeVorschaufehler] = useState<ApiFehler | undefined>(undefined);
  const [uebersteuerung, setzeUebersteuerung] = useState(false);
  const [erbringung, setzeErbringung] = useState<Erbringung>(auftrag.erbringung);
  const [fakturierung, setzeFakturierung] = useState<Fakturierung>(auftrag.fakturierung);
  const [grund, setzeGrund] = useState('');

  const stornierbar =
    !auftrag.eingefroren && darfBestaetigen && auftrag.moeglicheStatus.includes('storniert');
  /* AUF-15 (AeA-57): Ein Entwurf war kein Vertrag — er fällt ohne Gebühr weg. */
  const entwurf = auftrag.status === 'entwurf';

  /*
   * BUC-145: EIN Beleg für alle Stornovorgänge. Der Knopf steht, solange ein
   * wirksamer Vorgang noch ohne Beleg ist; danach zeigt die Leiste den Beleg.
   * Sind die Vorgänge nicht geladen, bleibt der Knopf — der Server sagt dann,
   * ob es etwas zu belegen gibt.
   */
  const wirksam = (vorgaenge ?? []).filter((v) => v.zurueckgenommenAm === null);
  const ohneBeleg = vorgaenge === undefined || wirksam.some((v) => v.beleg === null);
  const belege = [
    ...new Map(
      wirksam
        .filter((v) => v.beleg !== null)
        .map((v) => [v.beleg!, { id: v.beleg!, nummer: v.rechnungsnummer, status: v.belegstatus }]),
    ).values(),
  ];
  const festgeschriebenerBeleg = belege.find((b) => b.status === 'festgeschrieben');

  /* Eine Änderung an der Erklärung macht die Rechnung ungültig — sie galt dem alten Zugang. */
  function erklaerungAendern(wert: Erklaerungsangaben): void {
    setzeErklaerung(wert);
    setzeVorschau(undefined);
    setzeVorschaufehler(undefined);
  }

  function stornoSchliessen(): void {
    setzeStornoOffen(false);
    setzeVorschau(undefined);
    setzeVorschaufehler(undefined);
  }

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Stand</h2>
        {/* AUF-12, AUF-13 */}
      </div>

      <dl className="angaben">
        <dt>Status</dt>
        <dd>{STATUSNAME[auftrag.status]}</dd>
        <dt>Erbringung</dt>
        <dd>{ERBRINGUNGSNAME[auftrag.erbringung]}</dd>
        <dt>Fakturierung</dt>
        <dd>{FAKTURIERUNGSNAME[auftrag.fakturierung]}</dd>
        <dt>Herkunft</dt>
        <dd>
          {HERKUNFTSNAME[auftrag.herkunft]}
          {/* AUF-06: die Belegkette nach oben, anklickbar. */}
          {auftrag.angebot !== null && auftrag.angebotsnummer !== null && (
            <>
              {' · '}
              <button
                type="button"
                className="liste__oeffnen"
                onClick={() => beiAngebot(auftrag.angebot!)}
              >
                {auftrag.angebotsnummer}
              </button>
            </>
          )}
        </dd>
      </dl>

      {auftrag.uebersteuert && (
        <p className="warnung">
          Erbringung und Fakturierung stehen von Hand: „{auftrag.uebersteuerungsgrund}“. Solange
          das gilt, werden sie nicht mehr aus den Positionen fortgeschrieben.
        </p>
      )}

      {auftrag.storniertAm !== null && (
        <>
          <p className="warnung">
            {auftrag.bestaetigtAm === null ? 'Als Entwurf verworfen' : 'Storniert'}: „
            {auftrag.stornogrund}“.
          </p>
          {/*
            BUC-145: Der Storno rechnet die Gebuehren, die Faktura schreibt den
            Beleg — EINEN fuer den ganzen Auftrag (Auftraggeber, 14.09.2026).
            Er entsteht als Entwurf; die Nummer kommt erst mit der
            Festschreibung (RNG-03).
            ÄA-62 (B): Die Rückfrage bleibt. Der Beleg bindet die Vorgänge und sperrt
            das Herabsetzen (BUC-146); wer den Entwurf löscht, gibt sie nicht frei —
            das tut nur die Rücknahme (BUC-148).
          */}
          {darfFakturieren && auftrag.bestaetigtAm !== null && ohneBeleg && (
            <div className="knopfreihe">
              <button type="button" className="knopf" onClick={() => setzeFrage({ art: 'beleg' })}>
                Stornobeleg erzeugen …
              </button>
            </div>
          )}
          {belege.length > 0 && (
            <div className="knopfreihe">
              {belege.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => beiRechnung(b.id)}
                >
                  {b.nummer === null ? 'Stornobeleg (Entwurf) öffnen' : `Stornobeleg ${b.nummer} öffnen`}
                </button>
              ))}
            </div>
          )}

          {/*
            BUC-148 (AeA-58): DIE RUECKNAHME DES GANZEN STORNOS. Wo sie nicht
            geht, sagt die Leiste warum, statt einen Knopf ins Leere zu
            stellen; Termin und freie Plaetze prueft der Server und meldet sie.
          */}
          {darfBestaetigen && !auftrag.eingefroren && (
            auftrag.bestaetigtAm === null ? (
              <p className="feld__hilfe">
                Ein verworfener Entwurf war kein Vertrag und lässt sich nicht zurückholen. Legen
                Sie den Auftrag neu an.
              </p>
            ) : festgeschriebenerBeleg !== undefined ? (
              <p className="feld__hilfe">
                Der Stornobeleg {festgeschriebenerBeleg.nummer} ist festgeschrieben; der Storno
                lässt sich nicht mehr zurücknehmen. Ein neuer Auftrag bucht die Leistung erneut.
              </p>
            ) : ruecknahmeOffen ? (
              <Stornokorrektur
                id="auftrag-ruecknahme"
                art="zuruecknehmen"
                mitRueckfrage
                beiAbbrechen={() => setzeRuecknahmeOffen(false)}
                beiBestaetigen={({ grund: g }) => setzeFrage({ art: 'zuruecknehmen', grund: g })}
              />
            ) : (
              <div className="knopfreihe">
                <button type="button" className="knopf" onClick={() => setzeRuecknahmeOffen(true)}>
                  Storno zurücknehmen
                </button>
              </div>
            )
          )}
        </>
      )}

      {/*
        DIE KNOPFREIHE BLEIBT AM FESTGESCHRIEBENEN AUFTRAG STEHEN.
        Stornieren und „Neue Fassung anlegen" sind genau das, was dort noch zu
        tun ist — und der Weg aus der Sperre heraus. Nur die ABGELÖSTE Fassung
        bekommt keine Knöpfe: An ihr geschieht nichts mehr.
      */}
      {!auftrag.eingefroren && (
        <div className="knopfreihe">
          {stornierbar && !stornoOffen && (
            <button
              type="button"
              className="knopf knopf--eingriff"
              onClick={() => setzeStornoOffen(true)}
            >
              {entwurf ? 'Entwurf verwerfen' : 'Auftrag stornieren'}
            </button>
          )}
          {darfAendern && auftrag.status === 'bestaetigt' && (
            <button type="button" className="knopf" onClick={beiNeueVersion}>
              Neue Fassung anlegen
            </button>
          )}
          {darfUebersteuern && (
            <button
              type="button"
              className="knopf"
              onClick={() => setzeUebersteuerung(!uebersteuerung)}
            >
              {uebersteuerung ? 'Übersteuerung schließen' : 'Achsen übersteuern'}
            </button>
          )}
          {auftrag.moeglicheStatus.includes('bestaetigt') && darfBestaetigen && (
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => setzeFrage({ art: 'bestaetigen' })}
            >
              Auftrag bestätigen …
            </button>
          )}
        </div>
      )}

      {stornierbar && stornoOffen && entwurf && (
        <section className="karte karte--zweit" aria-labelledby="storno-titel">
          <h3 id="storno-titel">Entwurf verwerfen</h3>
          <p className="feld__hilfe">
            Ein Entwurf war noch kein Vertrag: Er fällt ohne Gebühr weg, und es entsteht kein
            Stornobeleg.
          </p>
          <Stornogrundfeld id="storno-grund" wert={stornogrund} beiAenderung={setzeStornogrund} />
          <div className="knopfreihe knopfreihe--abschluss">
            <button type="button" className="knopf" onClick={stornoSchliessen}>
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={stornogrund.trim() === ''}
              onClick={() => setzeFrage({ art: 'verwerfen', grund: stornogrund.trim() })}
            >
              Entwurf jetzt verwerfen …
            </button>
          </div>
        </section>
      )}

      {stornierbar && stornoOffen && !entwurf && (
        <section className="karte karte--zweit" aria-labelledby="storno-titel">
          <h3 id="storno-titel">Auftrag stornieren</h3>
          <Erklaerungsfelder id="storno" wert={erklaerung} beiAenderung={erklaerungAendern} />
          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={stornoSchliessen}>
              Abbrechen
            </button>
            <button
              type="button"
              className="knopf"
              disabled={erklaerung.zugang === ''}
              onClick={() =>
                void (async () => {
                  try {
                    setzeVorschau(await holeStornovorschau(auftrag.id, erklaerung.zugang));
                    setzeVorschaufehler(undefined);
                  } catch (ursache) {
                    setzeVorschau(undefined);
                    setzeVorschaufehler(ursache as ApiFehler);
                  }
                })()
              }
            >
              Storno berechnen
            </button>
          </div>

          {vorschaufehler !== undefined && <Meldung fehler={vorschaufehler} />}

          {vorschau !== undefined && (
            <Stornovorschautafel
              vorschau={vorschau}
              stornogrund={stornogrund}
              beiGrund={setzeStornogrund}
              beiStornieren={() =>
                setzeFrage({ art: 'stornieren', grund: stornogrund.trim(), erklaerung, vorschau })
              }
            />
          )}
        </section>
      )}

      {frage !== undefined && (
        <Statusrueckfrage
          auftrag={auftrag}
          frage={frage}
          vorgaenge={vorgaenge}
          beiSchliessen={() => setzeFrage(undefined)}
          beiBestaetigen={async () => {
            switch (frage.art) {
              case 'bestaetigen':
                await beiBestaetigen();
                break;
              case 'verwerfen':
                await beiStornieren(frage.grund, null, null);
                stornoSchliessen();
                break;
              case 'stornieren':
                await beiStornieren(frage.grund, frage.erklaerung, frage.vorschau);
                stornoSchliessen();
                break;
              case 'zuruecknehmen':
                await beiRuecknahme(frage.grund);
                setzeRuecknahmeOffen(false);
                break;
              case 'beleg':
                await beiStornobeleg();
                break;
            }
            setzeFrage(undefined);
          }}
        />
      )}

      {uebersteuerung && (
        <div className="karte">
          <div className="karte__titel">
            <h2>Achsen von Hand setzen</h2>
            {/* AUF-13 */}
          </div>
          <p className="feld__hilfe">
            Im Regelfall werden Erbringung und Fakturierung aus den Positionen fortgeschrieben.
            Diese Übersteuerung ist für Nacherfassungen aus dem Altsystem gedacht; sie hält die
            Fortschreibung an, verlangt einen Grund und wird protokolliert.
          </p>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="uebersteuerung-erbringung">
              Erbringung
            </label>
            <select
              id="uebersteuerung-erbringung"
              value={erbringung}
              onChange={(e) => setzeErbringung(e.target.value as Erbringung)}
            >
              {(Object.keys(ERBRINGUNGSNAME) as Erbringung[]).map((w) => (
                <option key={w} value={w}>
                  {ERBRINGUNGSNAME[w]}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="uebersteuerung-fakturierung">
              Fakturierung
            </label>
            <select
              id="uebersteuerung-fakturierung"
              value={fakturierung}
              onChange={(e) => setzeFakturierung(e.target.value as Fakturierung)}
            >
              {(Object.keys(FAKTURIERUNGSNAME) as Fakturierung[]).map((w) => (
                <option key={w} value={w}>
                  {FAKTURIERUNGSNAME[w]}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="uebersteuerung-grund">
              Grund
            </label>
            <input
              id="uebersteuerung-grund"
              type="text"
              value={grund}
              onChange={(e) => setzeGrund(e.target.value)}
              placeholder="Nacherfassung aus dem Altsystem"
            />
          </div>
          <div className="knopfreihe">
            {auftrag.uebersteuert && (
              <button
                type="button"
                className="knopf"
                onClick={() => beiAchsen({ erbringung, fakturierung, grund: null })}
              >
                Übersteuerung aufheben
              </button>
            )}
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={grund.trim() === ''}
              onClick={() => beiAchsen({ erbringung, fakturierung, grund })}
            >
              Übersteuern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Was die Statusleiste hinter einer Rückfrage auslöst (CRM-103). */
type Statusfrage =
  | { readonly art: 'bestaetigen' }
  | { readonly art: 'verwerfen'; readonly grund: string }
  | {
      readonly art: 'stornieren';
      readonly grund: string;
      readonly erklaerung: Erklaerungsangaben;
      readonly vorschau: Stornovorschau;
    }
  | { readonly art: 'zuruecknehmen'; readonly grund: string }
  | { readonly art: 'beleg' };

/** Summe von Beträgen in Cent gerechnet — Gleitkomma verliert sonst Cent. */
function summe(betraege: readonly string[]): string {
  const cent = betraege.reduce((s, b) => s + Math.round(Number(b) * 100), 0);
  return (cent / 100).toFixed(2);
}

/** „1 Zeile", „3 Zeilen" — die Zahl mit dem passenden Wort. */
function anzahl(n: number, eins: string, mehr: string): string {
  return `${n} ${n === 1 ? eins : mehr}`;
}

/** Die wirksame Gebühr eines Vorgangs: herabgesetzt, sonst gerechnet (BUC-146). */
function wirksameGebuehr(v: Auftragsvorgang): string {
  return v.herabgesetztAuf ?? v.gebuehr;
}

/** BUC-148: wann ein Storno zurückgeht — derselbe Satz an jeder Stelle. */
const RUECKNEHMBAR =
  'Zurücknehmen lässt er sich, solange kein Stornobeleg festgeschrieben ist, der Termin nicht begonnen hat und die Plätze noch frei sind.';

/**
 * DIE RÜCKFRAGEN DER STATUSLEISTE (CRM-103).
 *
 * Sie nennen, was die Maske schon weiß — die geladene Vorschau, die Vorgänge,
 * die Summe —, und rechnen nichts neu. Der Grund steht im Formular dahinter
 * und wird hier nur gezeigt, nicht noch einmal verlangt.
 */
function Statusrueckfrage({
  auftrag,
  frage,
  vorgaenge,
  beiBestaetigen,
  beiSchliessen,
}: {
  readonly auftrag: Auftrag;
  readonly frage: Statusfrage;
  readonly vorgaenge: readonly Auftragsvorgang[] | undefined;
  readonly beiBestaetigen: () => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const unterzeile = `${auftrag.auftragsnummer} · ${auftrag.kundenname}`;
  const wirksam = (vorgaenge ?? []).filter((v) => v.zurueckgenommenAm === null);

  switch (frage.art) {
    case 'bestaetigen': {
      /* BUC-75: gebucht werden Einzelplätze an einem Termin, die noch stehen. */
      const platzzeilen = auftrag.positionen.filter(
        (p) =>
          p.art === 'artikel'
          && p.vertriebsform === 'einzelplatz'
          && p.bezugsart === 'termin'
          && p.bezugId !== null
          && p.storniertAm === null,
      );
      return (
        <Rueckfrage
          frage="Auftrag bestätigen?"
          unterzeile={unterzeile}
          tat="Bestätigen"
          beiBestaetigen={beiBestaetigen}
          beiSchliessen={beiSchliessen}
        >
          {/* AUF-14: Die Bestätigung schreibt die Fassung fest. */}
          <p>
            Der Auftrag über {alsEuro(auftrag.summen.belegbrutto)} wird verbindlich, und diese
            Fassung wird festgeschrieben: Jede spätere Änderung braucht eine neue Fassung.
          </p>
          {platzzeilen.length > 0 && (
            <p>
              {/* Die Position weiß nicht, ob sie schon gebucht ist; eine Platzzahl wäre geraten. */}
              Für {anzahl(platzzeilen.length, 'Zeile', 'Zeilen')} mit Termin werden die Plätze
              gebucht, soweit sie es noch nicht sind.
            </p>
          )}
        </Rueckfrage>
      );
    }
    case 'verwerfen':
      return (
        <Rueckfrage
          frage="Entwurf verwerfen?"
          unterzeile={unterzeile}
          tat="Verwerfen"
          beiBestaetigen={beiBestaetigen}
          beiSchliessen={beiSchliessen}
        >
          {/* AUF-15 (AeA-57) */}
          <p>
            Der Entwurf über {alsEuro(auftrag.summen.belegbrutto)} fällt ohne Gebühr weg; es
            entsteht kein Stornobeleg. Ein verworfener Entwurf lässt sich nicht zurückholen.
          </p>
          <p>Grund: „{frage.grund}“</p>
        </Rueckfrage>
      );
    case 'stornieren': {
      const { vorschau } = frage;
      const gebucht = vorschau.posten.filter((p) => p.mitBuchung).length;
      return (
        <Rueckfrage
          frage="Auftrag stornieren?"
          unterzeile={unterzeile}
          tat="Stornieren"
          beiBestaetigen={beiBestaetigen}
          beiSchliessen={beiSchliessen}
        >
          {/* BUC-45, BUC-145: die Zahlen der geladenen Vorschau, nicht neu gerechnet. */}
          <p>
            {vorschau.posten.length === 0
              ? 'Keine Position trägt eine Gebühr.'
              : `Storniert ${vorschau.posten.length === 1 ? 'wird' : 'werden'} ${anzahl(vorschau.posten.length, 'Position', 'Positionen')}`
                + (gebucht > 0
                  ? `; die Plätze ${gebucht === 1 ? 'einer gebuchten Zeile' : `von ${gebucht} gebuchten Zeilen`} werden frei.`
                  : '.')}{' '}
            Zugang der Erklärung: {alsTag(vorschau.zugangAm)}.
          </p>
          <dl className="angaben">
            <dt>Gebühr</dt>
            <dd>{alsEuro(vorschau.gebuehr)}</dd>
            <dt>Zu zahlen</dt>
            <dd>{alsEuro(vorschau.zuBerechnen)}</dd>
            <dt>Zu erstatten</dt>
            <dd>{alsEuro(vorschau.zuErstatten)}</dd>
          </dl>
          <p>Grund: „{frage.grund}“</p>
          {/* BUC-148 */}
          <p className="feld__hilfe">{RUECKNEHMBAR}</p>
        </Rueckfrage>
      );
    }
    case 'zuruecknehmen': {
      const plaetze = wirksam
        .filter((v) => v.buchungsnummer !== null)
        .reduce((s, v) => s + v.plaetze, 0);
      return (
        <Rueckfrage
          frage="Storno zurücknehmen?"
          unterzeile={unterzeile}
          tat="Zurücknehmen"
          beiBestaetigen={beiBestaetigen}
          beiSchliessen={beiSchliessen}
        >
          {/* BUC-148 (AeA-58) */}
          <p>
            Der Auftrag steht wieder auf „{STATUSNAME.bestaetigt}“.
            {vorgaenge !== undefined && wirksam.length > 0 && (
              <>
                {' '}
                {anzahl(wirksam.length, 'Stornovorgang entfällt', 'Stornovorgänge entfallen')} samt
                {wirksam.length === 1 ? ' Gebühr von ' : ' Gebühren von zusammen '}
                {alsEuro(summe(wirksam.map(wirksameGebuehr)))}
                {plaetze > 0 && <>; {anzahl(plaetze, 'Platz wird', 'Plätze werden')} wieder belegt</>}.
              </>
            )}{' '}
            Ein Belegentwurf zum Storno wird verworfen.
          </p>
          <p>Grund: „{frage.grund}“</p>
        </Rueckfrage>
      );
    }
    case 'beleg': {
      const offen = wirksam.filter((v) => v.beleg === null);
      return (
        <Rueckfrage
          frage="Stornobeleg erzeugen?"
          unterzeile={unterzeile}
          tat="Erzeugen"
          beiBestaetigen={beiBestaetigen}
          beiSchliessen={beiSchliessen}
        >
          {/* BUC-145, RNG-03 */}
          <p>
            EIN Beleg nimmt{' '}
            {vorgaenge === undefined
              ? 'alle Stornovorgänge ohne Beleg'
              : `${anzahl(offen.length, 'Stornovorgang', 'Stornovorgänge')} mit ${offen.length === 1 ? 'einer Gebühr von' : 'Gebühren von zusammen'} ${alsEuro(summe(offen.map(wirksameGebuehr)))}`}{' '}
            auf. Er entsteht als Entwurf; die Nummer kommt mit der Festschreibung.
          </p>
          {/* BUC-146 (AeA-59): Herabgesetzt wird nur ohne Beleg. */}
          <p className="feld__hilfe">Danach lässt sich keine dieser Gebühren mehr herabsetzen.</p>
        </Rueckfrage>
      );
    }
  }
}

/** Der Grund eines Stornos — Pflicht, mit Marke wie im Service. */
function Stornogrundfeld({
  id,
  wert,
  beiAenderung,
  beschriftung = 'Grund des Stornos',
  hilfe,
}: {
  readonly id: string;
  readonly wert: string;
  readonly beiAenderung: (wert: string) => void;
  readonly beschriftung?: string;
  readonly hilfe?: string;
}): JSX.Element {
  return (
    <div className="feld">
      <label className="feld__beschriftung" htmlFor={id}>
        {beschriftung} <span className="feld__pflicht">Pflicht</span>
      </label>
      <input
        id={id}
        type="text"
        className="feld__eingabe"
        value={wert}
        maxLength={2000}
        onChange={(e) => beiAenderung(e.target.value)}
        placeholder="Kunde hat abgesagt"
      />
      {hilfe !== undefined && <p className="feld__hilfe">{hilfe}</p>}
    </div>
  );
}

/**
 * AUF-15 (AeA-57): warum eine Zeile ohne Gebühr wegfällt — in Worten, nicht
 * als Kennung der Staffel.
 */
const OHNE_GEBUEHR: Readonly<Record<NonNullable<Stornoposten['ohneGebuehr']>, string>> = {
  entwurf: 'ohne Gebühr, weil der Auftrag ein Entwurf ist',
  keine_stufe: 'ohne Gebühr, weil keine Staffel greift',
  keine_staffel: 'ohne Gebühr, weil für diese Vertragsart keine Stornostaffel hinterlegt ist',
  kein_tag: 'ohne Gebühr, weil kein Termin feststeht',
};

/** Satz oder Grund, warum keiner gilt. */
function satzText(posten: Stornoposten): string {
  return posten.ohneGebuehr === null ? alsProzent(posten.satzProzent) : OHNE_GEBUEHR[posten.ohneGebuehr];
}

/**
 * WAS DER STORNO KOSTET — VOR DER AUSFÜHRUNG (BUC-45, BUC-145, AUF-15).
 *
 * BUC-45 wörtlich: „Die Berechnung wird vor der Ausführung angezeigt." Bis
 * hierher setzte der Knopf „Stornieren" den Status und ging; niemand im
 * Innendienst konnte dem Kunden am Telefon sagen, was sein Rücktritt kostet,
 * ohne ihn zu vollziehen.
 *
 * JE ZEILE EINE ZEILE. AUF-15 verlangt den Storno „je Position …, nicht
 * pauschal für den Auftrag", und die Gebühr bemisst sich am Abstand zum
 * Termin — ein Auftrag mit einem Training nächste Woche und einem im März hat
 * zwei Sätze. Die Tafel nennt je Zeile den Terminbeginn (FRS-19: von dort
 * läuft die Frist) und den Satz, damit die Zahl nachvollziehbar bleibt. Die
 * Kennung der Staffel steht nicht da — sie ist ein Schlüssel, kein Wort.
 */
function Stornovorschautafel({
  vorschau,
  stornogrund,
  beiGrund,
  beiStornieren,
}: {
  readonly vorschau: Stornovorschau;
  readonly stornogrund: string;
  readonly beiGrund: (wert: string) => void;
  readonly beiStornieren: () => void;
}): JSX.Element {
  const hinweise = vorschau.posten.flatMap((p) => p.hinweise);

  return (
    <div>
      <h4>Was der Storno kostet</h4>
      {/* BUC-45, BUC-145 */}

      {vorschau.posten.length === 0 ? (
        <p className="feld__hilfe">
          Keine stornierbare Position. Der Auftrag lässt sich stornieren, ohne dass eine Gebühr
          entsteht.
        </p>
      ) : (
        <div className="stornotafel">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th scope="col">Position</th>
                <th scope="col">Termin</th>
                <th scope="col" className="zahl">
                  Satz
                </th>
                <th scope="col" className="zahl">
                  Grundbetrag
                </th>
                <th scope="col" className="zahl">
                  Gebühr
                </th>
                <th scope="col" className="zahl">
                  verrechnet
                </th>
                <th scope="col" className="zahl">
                  zu zahlen
                </th>
                <th scope="col" className="zahl">
                  zu erstatten
                </th>
              </tr>
            </thead>
            <tbody>
              {vorschau.posten.map((p) => (
                <tr key={p.position}>
                  <td>
                    {p.bezeichnung}
                    {/*
                      Ohne Buchung hält die Zeile keinen Teilnehmerplatz — die
                      Gebühr fällt trotzdem an (AGB § 6, § 7).
                    */}
                    {!p.mitBuchung && <span className="marke">ohne Buchung</span>}
                  </td>
                  <td>{alsZeitpunkt(p.terminbeginn) ?? 'kein Termin'}</td>
                  <td className="zahl">{satzText(p)}</td>
                  <td className="zahl">{alsEuro(p.grundbetrag)}</td>
                  <td className="zahl">{alsEuro(p.gebuehr)}</td>
                  <td className="zahl">{alsEuro(p.verrechnet)}</td>
                  <td className="zahl">{alsEuro(p.zuBerechnen)}</td>
                  <td className="zahl">{alsEuro(p.zuErstatten)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row" colSpan={4}>
                  Gesamt
                </th>
                <td className="zahl">
                  <strong>{alsEuro(vorschau.gebuehr)}</strong>
                </td>
                <td className="zahl">{alsEuro(vorschau.verrechnet)}</td>
                <td className="zahl">
                  <strong>{alsEuro(vorschau.zuBerechnen)}</strong>
                </td>
                <td className="zahl">
                  <strong>{alsEuro(vorschau.zuErstatten)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Die Summe zum Mitlesen: Sie ändert sich mit jeder neuen Berechnung. */}
      <p className="hinweiszeile" role="status">
        Gebühr {alsEuro(vorschau.gebuehr)} · zu zahlen {alsEuro(vorschau.zuBerechnen)} · zu
        erstatten {alsEuro(vorschau.zuErstatten)}
      </p>

      {/* BUC-145: Was nicht verrechnet werden konnte, steht hier und nicht im Protokoll. */}
      {hinweise.length > 0 && (
        <ul className="warnung">
          {hinweise.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      )}

      {/* BUC-144, FRS-19 */}
      <p className="feld__hilfe">
        Gerechnet wird vom Zugang der Erklärung bis zum Beginn des Termins — für alle Zeilen mit
        demselben Zugang. Das Stornieren schreibt die Gebühren fest und gibt die Plätze zurück;
        der Beleg entsteht danach in einem eigenen Schritt.
      </p>

      <Stornogrundfeld id="storno-grund" wert={stornogrund} beiAenderung={beiGrund} />

      <div className="knopfreihe knopfreihe--abschluss">
        <button
          type="button"
          className="knopf knopf--eingriff"
          disabled={stornogrund.trim() === ''}
          onClick={beiStornieren}
        >
          Jetzt stornieren …
        </button>
      </div>
    </div>
  );
}

/**
 * DIE STORNOVORGÄNGE DES AUFTRAGS (BUC-145, BUC-146, BUC-148).
 *
 * Herabsetzen ist ein eigener Aufruf an einem bestehenden Vorgang (AeA-59) —
 * er gehört deshalb hierher, hinter den Storno, und nicht in die Vorschau
 * davor. Zurückgenommen wird hier der Storno einzelner Plätze; eine ganze
 * Zeile nimmt die Zeile selbst zurück, den ganzen Auftrag die Statusleiste.
 */
function Stornovorgaenge({
  auftrag,
  vorgaenge,
  darfHerabsetzen,
  darfZuruecknehmen,
  beiHerabsetzen,
  beiRuecknahme,
}: {
  readonly auftrag: Auftrag;
  readonly vorgaenge: readonly Auftragsvorgang[];
  readonly darfHerabsetzen: boolean;
  readonly darfZuruecknehmen: boolean;
  readonly beiHerabsetzen: (vorgang: string, betrag: string, grund: string) => Promise<void>;
  readonly beiRuecknahme: (vorgang: string, grund: string) => Promise<void>;
}): JSX.Element {
  const [offen, setzeOffen] = useState<
    { readonly vorgang: string; readonly art: 'herabsetzen' | 'zuruecknehmen' } | undefined
  >(undefined);
  /* CRM-103: Das Formular bleibt hinter der Rückfrage stehen — Abbrechen kostet keine Eingabe. */
  const [frage, setzeFrage] = useState<
    | {
        readonly vorgang: Auftragsvorgang;
        readonly art: 'herabsetzen' | 'zuruecknehmen';
        readonly betrag: string | null;
        readonly grund: string;
      }
    | undefined
  >(undefined);

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Stornos</h2>
        <small>{vorgaenge.length}</small>
      </div>
      <div className="tafel--weit">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Position</th>
              <th scope="col" className="zahl">
                Plätze
              </th>
              <th scope="col">Erklärung</th>
              <th scope="col" className="zahl">
                Gebühr
              </th>
              <th scope="col" className="zahl">
                zu zahlen
              </th>
              <th scope="col" className="zahl">
                zu erstatten
              </th>
              <th scope="col">Beleg</th>
              <th scope="col">Handlung</th>
            </tr>
          </thead>
          <tbody>
            {vorgaenge.map((v) => {
              const zurueck = v.zurueckgenommenAm !== null;
              /* BUC-146 (AeA-59): nur ohne Beleg. */
              const herabsetzbar = darfHerabsetzen && !zurueck && v.beleg === null;
              /* BUC-148: einzelne Plätze hier; eine ganze Zeile oder den Auftrag dort. */
              const ruecknehmbar =
                darfZuruecknehmen
                && !zurueck
                && !v.positionStorniert
                && auftrag.status !== 'storniert'
                && v.belegstatus !== 'festgeschrieben';
              return (
                <Fragment key={v.id}>
                  <tr>
                    <td>
                      {v.bezeichnung ?? '—'}
                      {zurueck && <span className="marke marke--inaktiv">zurückgenommen</span>}
                    </td>
                    <td className="zahl">{v.plaetze}</td>
                    <td>
                      {alsTag(v.zugangAm)}
                      {v.erklaerungsweg !== null && (
                        <>
                          {' · '}
                          {ERKLAERUNGSWEGNAME[v.erklaerungsweg as keyof typeof ERKLAERUNGSWEGNAME]
                            ?? v.erklaerungsweg}
                        </>
                      )}
                      {v.erklaerungsverweis !== null && (
                        <span className="feld__hilfe"> — {v.erklaerungsverweis}</span>
                      )}
                    </td>
                    <td className="zahl">
                      {v.herabgesetztAuf === null ? (
                        alsEuro(v.gebuehr)
                      ) : (
                        <>
                          <s>{alsEuro(v.gebuehr)}</s> {alsEuro(v.herabgesetztAuf)}
                        </>
                      )}
                    </td>
                    <td className="zahl">{alsEuro(v.zuBerechnen)}</td>
                    <td className="zahl">{alsEuro(v.zuErstatten)}</td>
                    <td>
                      {v.beleg === null
                        ? '—'
                        : v.rechnungsnummer ?? 'Entwurf'}
                    </td>
                    <td>
                      {herabsetzbar && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => setzeOffen({ vorgang: v.id, art: 'herabsetzen' })}
                        >
                          Herabsetzen
                        </button>
                      )}
                      {ruecknehmbar && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => setzeOffen({ vorgang: v.id, art: 'zuruecknehmen' })}
                        >
                          Zurücknehmen
                        </button>
                      )}
                    </td>
                  </tr>
                  {v.herabsetzungGrund !== null && (
                    <tr>
                      <td colSpan={8}>
                        <p className="feld__hilfe">Herabgesetzt: „{v.herabsetzungGrund}“.</p>
                      </td>
                    </tr>
                  )}
                  {offen?.vorgang === v.id && (
                    /* CRM-106: auf dem Telefon im Dialog statt in der rollenden Zeile. */
                    <Zeilenformular
                      spalten={8}
                      titel={offen.art === 'herabsetzen' ? 'Gebühr herabsetzen' : 'Storno zurücknehmen'}
                      unterzeile={v.bezeichnung ?? undefined}
                      beiSchliessen={() => setzeOffen(undefined)}
                    >
                      <Stornokorrektur
                        id={`vorgang-${v.id}`}
                        art={offen.art}
                        gebuehr={wirksameGebuehr(v)}
                        mitRueckfrage
                        beiAbbrechen={() => setzeOffen(undefined)}
                        beiBestaetigen={({ betrag, grund }) =>
                          setzeFrage({ vorgang: v, art: offen.art, betrag, grund })
                        }
                      />
                    </Zeilenformular>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {frage !== undefined && (
        <Korrekturrueckfrage
          vorgang={frage.vorgang}
          art={frage.art}
          betrag={frage.betrag}
          grund={frage.grund}
          beiSchliessen={() => setzeFrage(undefined)}
          beiBestaetigen={async () => {
            if (frage.art === 'herabsetzen' && frage.betrag !== null) {
              await beiHerabsetzen(frage.vorgang.id, frage.betrag, frage.grund);
            } else {
              await beiRuecknahme(frage.vorgang.id, frage.grund);
            }
            setzeFrage(undefined);
            setzeOffen(undefined);
          }}
        />
      )}
    </div>
  );
}

/**
 * DIE RÜCKFRAGE ZU HERABSETZUNG UND RÜCKNAHME EINES VORGANGS (CRM-103).
 *
 * Betrag und Grund kommen aus der Stornokorrektur dahinter; das Pop-up nennt
 * sie und verlangt sie nicht noch einmal.
 */
function Korrekturrueckfrage({
  vorgang,
  art,
  betrag,
  grund,
  beiBestaetigen,
  beiSchliessen,
}: {
  readonly vorgang: Auftragsvorgang;
  readonly art: 'herabsetzen' | 'zuruecknehmen';
  readonly betrag: string | null;
  readonly grund: string;
  readonly beiBestaetigen: () => Promise<void>;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const unterzeile = [vorgang.bezeichnung, vorgang.buchungsnummer]
    .filter((t): t is string => t !== null)
    .join(' · ');
  if (art === 'herabsetzen') {
    return (
      <Rueckfrage
        frage="Gebühr herabsetzen?"
        unterzeile={unterzeile === '' ? undefined : unterzeile}
        tat="Herabsetzen"
        beiBestaetigen={beiBestaetigen}
        beiSchliessen={beiSchliessen}
      >
        {/* BUC-146 (AeA-59) */}
        <p>
          Die Gebühr sinkt von {alsEuro(wirksameGebuehr(vorgang))} auf {alsEuro(betrag)}. Das geht
          nur, solange kein Beleg erzeugt ist.
        </p>
        <p>Grund: „{grund}“</p>
      </Rueckfrage>
    );
  }
  return (
    <Rueckfrage
      frage="Storno zurücknehmen?"
      unterzeile={unterzeile === '' ? undefined : unterzeile}
      tat="Zurücknehmen"
      beiBestaetigen={beiBestaetigen}
      beiSchliessen={beiSchliessen}
    >
      {/* BUC-148 (AeA-58) */}
      <p>
        {vorgang.buchungsnummer !== null
          ? `${anzahl(vorgang.plaetze, 'Platz wird', 'Plätze werden')} wieder belegt, die Gebühr`
          : 'Die Gebühr'}{' '}
        von {alsEuro(wirksameGebuehr(vorgang))} entfällt, und ein Belegentwurf zum Storno wird
        verworfen.
      </p>
      <p>Grund: „{grund}“</p>
    </Rueckfrage>
  );
}

function Positionen({
  auftrag,
  bearbeitbar,
  darfStornieren,
  vorgaenge,
  beiAnlegen,
  beiLoeschen,
  beiTerminwahl,
  beiStornieren,
  beiRuecknahme,
  beiOrdnen,
}: {
  readonly auftrag: Auftrag;
  readonly bearbeitbar: boolean;
  /**
   * AUF-15, AUF-14: Stornieren hängt am Recht und an der geltenden Fassung,
   * NICHT an der Festschreibung — der Teilstorno läuft an der Fassung beim
   * Kunden weiter (Entscheidung des Auftraggebers zu Auftrag CRM-98).
   */
  readonly darfStornieren: boolean;
  readonly vorgaenge: readonly Auftragsvorgang[] | undefined;
  readonly beiAnlegen: (eingabe: Parameters<typeof positionAnlegen>[1]) => void;
  readonly beiLoeschen: (position: string) => Promise<void>;
  /** L5 (CRM-116): der Termin einer Einzelplatzzeile im Entwurf. */
  readonly beiTerminwahl: (position: string, eingabe: Terminwahl) => Promise<void>;
  readonly beiStornieren: (position: string, eingabe: Teilstorno) => Promise<void>;
  readonly beiRuecknahme: (position: string, grund: string) => Promise<void>;
  readonly beiOrdnen: (reihenfolge: readonly string[]) => void;
}): JSX.Element {
  const [offen, setzeOffen] = useState<string | undefined>(undefined);

  /*
   * Die Exklusivtermine dieses Kunden — einmal geladen. Das Formular bietet
   * sie zur Wahl, die Zeilen schreiben ihren Termin aus. Wortgleich mit der
   * Angebotsakte, und aus demselben Grund: Was am Angebot gewählt wurde,
   * muss am Auftrag genauso dastehen, sonst liest sich derselbe Vorgang auf
   * zwei Belegen verschieden.
   */
  /* ERR-01: „keiner vereinbart" und „nicht erreichbar" sind zweierlei. */
  const [termine, setzeTermine] = useState<readonly Exklusivtermin[]>([]);
  const [terminfehler, setzeTerminfehler] = useState(false);
  useEffect(() => {
    let verworfen = false;
    void exklusive(auftrag.kunde)
      .then((e) => {
        if (verworfen) return;
        setzeTermine(e);
        setzeTerminfehler(false);
      })
      .catch(() => {
        if (verworfen) return;
        setzeTermine([]);
        setzeTerminfehler(true);
      });
    return () => {
      verworfen = true;
    };
  }, [auftrag.kunde]);

  function verschiebe(index: number, richtung: -1 | 1): void {
    const ids = auftrag.positionen.map((p) => p.id);
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= ids.length) return;
    const kopie = [...ids];
    [kopie[index], kopie[ziel]] = [kopie[ziel]!, kopie[index]!];
    beiOrdnen(kopie);
  }

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Positionen</h2>
        <small>{auftrag.positionen.length}</small>
      </div>

      <div className="tafel--weit">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Art</th>
              <th scope="col">Bezeichnung</th>
              <th scope="col">Bezug</th>
              <th scope="col" className="zahl">
                Menge
              </th>
              <th scope="col" className="zahl">
                offen
              </th>
              <th scope="col" className="zahl">
                Einzelpreis
              </th>
              <th scope="col" className="zahl">
                Netto
              </th>
              <th scope="col">Handlung</th>
            </tr>
          </thead>
          <tbody>
            {auftrag.positionen.map((p, i) => (
              <Positionszeile
                termine={termine}
                key={p.id}
                position={p}
                nummer={i + 1}
                bearbeitbar={bearbeitbar}
                erste={i === 0}
                letzte={i === auftrag.positionen.length - 1}
                offen={offen === p.id}
                beiAufklappen={() => setzeOffen(offen === p.id ? undefined : p.id)}
                beiHoch={() => verschiebe(i, -1)}
                beiRunter={() => verschiebe(i, 1)}
                beiLoeschen={() => beiLoeschen(p.id)}
                beiTerminwahl={(eingabe) => beiTerminwahl(p.id, eingabe)}
                kunde={auftrag.kunde}
                kundenart={auftrag.kundenart}
                auftrag={auftrag.id}
                entwurf={auftrag.status === 'entwurf'}
                darfStornieren={darfStornieren && auftrag.status !== 'storniert'}
                stornovorgang={vorgaenge?.find(
                  (v) => v.position === p.id && v.positionStorniert && v.zurueckgenommenAm === null,
                )}
                beiStornieren={(eingabe) => beiStornieren(p.id, eingabe)}
                beiRuecknahme={(grund) => beiRuecknahme(p.id, grund)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* AUF-01 */}
      {auftrag.positionen.length === 0 && (
        <p className="feld__hilfe">
          Noch keine Position. Ein Auftrag ohne Leistung hält keinen Vertragsstand fest.
        </p>
      )}

      {bearbeitbar && (
        <NeuePosition
          termine={termine}
          terminfehler={terminfehler}
          kunde={auftrag.kunde}
          kundenart={auftrag.kundenart}
          beiAnlegen={beiAnlegen}
        />
      )}
    </div>
  );
}

function Positionszeile({
  position,
  termine,
  nummer,
  bearbeitbar,
  erste,
  letzte,
  offen,
  beiAufklappen,
  beiHoch,
  beiRunter,
  beiLoeschen,
  beiTerminwahl,
  kunde,
  kundenart,
  auftrag,
  entwurf,
  darfStornieren,
  stornovorgang,
  beiStornieren,
  beiRuecknahme,
}: {
  readonly position: Auftragsposition;
  readonly termine: readonly Exklusivtermin[];
  readonly nummer: number;
  readonly bearbeitbar: boolean;
  readonly erste: boolean;
  readonly letzte: boolean;
  readonly offen: boolean;
  readonly beiAufklappen: () => void;
  readonly beiHoch: () => void;
  readonly beiRunter: () => void;
  readonly beiLoeschen: () => Promise<void>;
  readonly beiTerminwahl: (eingabe: Terminwahl) => Promise<void>;
  readonly kunde: string;
  /** BUC-105: nur Termine, die für die Kundenart des Auftrags buchbar sind. */
  readonly kundenart: string;
  readonly auftrag: string;
  /** AUF-15 (AeA-57): Am Entwurf fällt die Zeile ohne Gebühr und ohne Zugang weg. */
  readonly entwurf: boolean;
  readonly darfStornieren: boolean;
  /** Der Vorgang, mit dem die ganze Zeile wegfiel — für die Rücknahme. */
  readonly stornovorgang: Auftragsvorgang | undefined;
  readonly beiStornieren: (eingabe: Teilstorno) => Promise<void>;
  readonly beiRuecknahme: (grund: string) => Promise<void>;
}): JSX.Element {
  const [stornoOffen, setzeStornoOffen] = useState(false);
  const [ruecknahmeOffen, setzeRuecknahmeOffen] = useState(false);
  /* CRM-103: die Rückfragen zu Entfernen und Rücknahme dieser Zeile. */
  const [entfernenFrage, setzeEntfernenFrage] = useState(false);
  const [ruecknahmegrund, setzeRuecknahmegrund] = useState<string | undefined>(undefined);
  const storniert = position.storniertAm !== null;
  const berechnet = Number(position.berechneteMenge) > 0;
  /*
   * L5 (CRM-116), AUF-17: Im Entwurf wird der Termin einer Einzelplatzzeile
   * an der Zeile gewählt oder gewechselt. Nach der Bestätigung hängen die
   * Plätze an einer Buchung — dann ist es eine Umbuchung im Buchungsvorgang.
   */
  const [terminwahlOffen, setzeTerminwahlOffen] = useState(false);
  const terminwaehlbar =
    bearbeitbar && entwurf && !storniert && position.artikel !== null
    && position.vertriebsform === 'einzelplatz' && position.bezugsart === 'termin';

  return (
    <>
      <tr>
        <td>{nummer}</td>
        <td>
          {/* AUF-04: das Wort kommt vom Server (fach.positionsart), nicht von hier. */}
          {POSITIONSARTNAME[position.positionsart]}
        </td>
        <td>
          {position.bezeichnung}
          {position.artikelnummer !== null && (
            <span className="kanalliste__name"> · {position.artikelnummer}</span>
          )}
          {/* PRD-42: die Vertriebsform, sobald sie nicht der Regelfall ist. */}
          {position.vertriebsform !== null && position.vertriebsform !== 'einzelplatz' && (
            <span className="marke">{VERTRIEBSFORMNAME[position.vertriebsform]}</span>
          )}
          {storniert && <span className="marke marke--gesperrt">storniert</span>}
        </td>
        <td>
          {/*
            AUF-05: WORAUF SICH DIE LEISTUNG BEZIEHT.
            Hier stand das nackte Wort „termin" — die Bezugsart ohne den
            Bezug. Es sagte nur, dass ein Termin gemeint SEI, nicht welcher,
            und bei den Zeilen aus einer Angebotsübernahme war gar keiner
            gemeint: Die Übernahme setzte die Art und liess die Kennung leer.
          */}
          {position.bezugsart === null ? (
            <span className="feld__hilfe">—</span>
          ) : position.bezugsart === 'termin' ? (
            <>
              <span className="feld__hilfe">{terminbezug(position, termine)}</span>
              {terminwaehlbar && (
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => setzeTerminwahlOffen(true)}
                >
                  {position.bezugId === null ? 'Termin wählen' : 'Termin ändern'}
                </button>
              )}
              {terminwahlOffen && position.artikel !== null && (
                <Terminwahlfenster
                  position={position}
                  artikel={position.artikel}
                  kunde={kunde}
                  kundenart={kundenart}
                  beiSchliessen={() => setzeTerminwahlOffen(false)}
                  beiUebernahme={async (eingabe) => {
                    await beiTerminwahl(eingabe);
                    setzeTerminwahlOffen(false);
                  }}
                />
              )}
            </>
          ) : position.bezugsart === 'zeitraum' && position.bezugVon !== null ? (
            <span className="feld__hilfe">
              ab {alsTag(position.bezugVon)}
              {position.bezugBis !== null && ` bis ${String(alsTag(position.bezugBis))}`}
            </span>
          ) : (
            <span className="feld__hilfe">{BEZUGSNAME[position.bezugsart]}</span>
          )}
        </td>
        <td className="zahl">{alsMengenangabe(position.menge, position.mengeneinheit)}</td>
        <td className="zahl">
          {/* AUF-20: was noch nicht berechnet ist. */}
          {alsMenge(position.offeneMenge)}
          {berechnet && !storniert && (
            <span className="marke marke--inaktiv">
              {alsMenge(position.berechneteMenge)} berechnet
            </span>
          )}
        </td>
        <td className="zahl">
          {alsEuro(position.einzelpreis)}
          {/*
            PRD-12: DASS DER PREIS DIE STEUER ENTHAELT, MUSS DASTEHEN.
            Sonst liest der Anwender „1 × 3.200,00 €" neben einem Netto von
            2.689,07 € und findet keinen Zusammenhang. Die Belegnaht rechnet
            zurück (RND-08); die Marke sagt, warum.
          */}
          {position.istBrutto === true && (
            <span className="marke marke--inaktiv">brutto</span>
          )}
          {position.preisUebersteuert && <span className="marke marke--inaktiv">von Hand</span>}
        </td>
        <td className="zahl">{storniert ? '—' : alsEuro(position.netto)}</td>
        <td>
          {position.herleitung.length > 0 && (
            <button type="button" className="knopf knopf--klein" onClick={beiAufklappen}>
              {offen ? 'Herleitung zu' : 'Herleitung'}
            </button>
          )}
          {bearbeitbar && !storniert && (
            <>
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={erste}
                onClick={beiHoch}
                aria-label={`Position ${nummer} nach oben`}
              >
                ↑
              </button>
              <button
                type="button"
                className="knopf knopf--klein"
                disabled={letzte}
                onClick={beiRunter}
                aria-label={`Position ${nummer} nach unten`}
              >
                ↓
              </button>
              {/*
                AUF-15 gegen Entfernen: Eine noch nicht berechnete Zeile kann
                weg; eine berechnete wird storniert und bleibt stehen, weil
                sie auf einer Rechnung steht.
              */}
              {/* AUF-16 */}
              {berechnet ? (
                <span className="feld__hilfe">berechnet</span>
              ) : (
                <button
                  type="button"
                  className="knopf knopf--eingriff knopf--klein"
                  onClick={() => setzeEntfernenFrage(true)}
                >
                  Entfernen …
                </button>
              )}
              {entfernenFrage && (
                <Rueckfrage
                  frage="Position entfernen?"
                  unterzeile={`Position ${nummer} · ${position.bezeichnung}`}
                  tat="Entfernen"
                  beiSchliessen={() => setzeEntfernenFrage(false)}
                  beiBestaetigen={async () => {
                    await beiLoeschen();
                    setzeEntfernenFrage(false);
                  }}
                >
                  {/* Der Server löscht die Zeile; anders als beim Storno bleibt nichts stehen. */}
                  <p>
                    Die Zeile
                    {position.menge !== null
                      && ` über ${alsMengenangabe(position.menge, position.mengeneinheit)}`}
                    {position.netto !== null && ` (netto ${alsEuro(position.netto)})`} wird
                    gelöscht. Sie lässt sich nicht wiederherstellen, nur neu anlegen.
                  </p>
                </Rueckfrage>
              )}
            </>
          )}
          {/* AUF-15 (AeA-57): gesperrt für berechnete und stornierte Zeilen. */}
          {darfStornieren && !storniert && !berechnet && STORNIERBAR.includes(position.art) && (
            <button
              type="button"
              className="knopf knopf--eingriff knopf--klein"
              aria-expanded={stornoOffen}
              onClick={() => setzeStornoOffen(!stornoOffen)}
            >
              Stornieren
            </button>
          )}
        </td>
      </tr>

      {stornoOffen && (
        /* CRM-106: auf dem Telefon im Dialog statt in der rollenden Zeile. */
        <Zeilenformular
          spalten={9}
          titel="Position stornieren"
          unterzeile={`Position ${String(nummer)} · ${position.bezeichnung}`}
          beiSchliessen={() => setzeStornoOffen(false)}
        >
          <Teilstornoformular
            auftrag={auftrag}
            position={position}
            nummer={nummer}
            entwurf={entwurf}
            beiAbbrechen={() => setzeStornoOffen(false)}
            beiStornieren={async (eingabe) => {
              await beiStornieren(eingabe);
              setzeStornoOffen(false);
            }}
          />
        </Zeilenformular>
      )}

      {storniert && (
        <tr>
          <td colSpan={9}>
            <p className="feld__hilfe">Storniert: „{position.stornogrund}“.</p>
            {/* BUC-148 (AeA-58): die ganze Zeile zurück, solange kein Beleg festgeschrieben ist. */}
            {darfStornieren && stornovorgang !== undefined && (
              stornovorgang.belegstatus === 'festgeschrieben' ? (
                <p className="feld__hilfe">
                  Der Stornobeleg {stornovorgang.rechnungsnummer} ist festgeschrieben; der Storno
                  dieser Zeile lässt sich nicht mehr zurücknehmen.
                </p>
              ) : ruecknahmeOffen ? (
                /* CRM-106: auf dem Telefon im Dialog statt in der rollenden Zeile. */
                <Telefonfenster
                  titel="Storno zurücknehmen"
                  unterzeile={`Position ${String(nummer)} · ${position.bezeichnung}`}
                  beiSchliessen={() => setzeRuecknahmeOffen(false)}
                >
                  <Stornokorrektur
                    id={`zeile-ruecknahme-${position.id}`}
                    art="zuruecknehmen"
                    mitRueckfrage
                    beiAbbrechen={() => setzeRuecknahmeOffen(false)}
                    beiBestaetigen={({ grund }) => setzeRuecknahmegrund(grund)}
                  />
                  {ruecknahmegrund !== undefined && (
                    <Korrekturrueckfrage
                      vorgang={stornovorgang}
                      art="zuruecknehmen"
                      betrag={null}
                      grund={ruecknahmegrund}
                      beiSchliessen={() => setzeRuecknahmegrund(undefined)}
                      beiBestaetigen={async () => {
                        await beiRuecknahme(ruecknahmegrund);
                        setzeRuecknahmegrund(undefined);
                        setzeRuecknahmeOffen(false);
                      }}
                    />
                  )}
                </Telefonfenster>
              ) : (
                <div className="knopfreihe">
                  <button
                    type="button"
                    className="knopf knopf--klein"
                    onClick={() => setzeRuecknahmeOffen(true)}
                  >
                    Storno zurücknehmen
                  </button>
                </div>
              )
            )}
          </td>
        </tr>
      )}

      {offen && (
        <tr>
          <td colSpan={9}>
            <p className="feld__hilfe">
              Preisherkunft: {position.preisherkunft ?? 'unbekannt'}
              {position.hoechstrabattProzent !== null
                && ` · Höchstrabatt ${position.hoechstrabattProzent} %`}
              {position.angebotsposition !== null && ' · aus dem Angebot übernommen'}
            </p>
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <thead>
                  <tr>
                    <th scope="col">Stufe</th>
                    <th scope="col">Regel</th>
                    <th scope="col" className="zahl">
                      davor
                    </th>
                    <th scope="col" className="zahl">
                      danach
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {position.herleitung.map((h, i) => (
                    <tr key={`${h.stufe}-${h.regelkennung}-${String(i)}`}>
                      <td>{h.stufe}</td>
                      <td>
                        {h.regelart}
                        <span className="kanalliste__name"> · {h.regelkennung}</span>
                        {h.gekappt === true && <span className="marke marke--gesperrt">gekappt</span>}
                      </td>
                      <td className="zahl">{h.wertDavor}</td>
                      <td className="zahl">{h.wertDanach}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Eine neue Position (AUF-03 bis AUF-05).
 *
 * DER LEISTUNGSBEZUG IST PFLICHT, sobald ein Artikel im Spiel ist. AUF-05
 * lässt eine Position ohne Bezug nur als Freitext zu — ohne ihn entstünde
 * keine Verbindung zu Tagesplan und Durchführung. Vorbelegt ist „Termin",
 * weil fast jede Auftragsposition an einem Trainingstermin hängt.
 */
/** Was der Teilstorno einer Zeile an den Server gibt — und was er meldet. */
type Teilstorno = {
  readonly grund: string;
  /** `null` am Entwurf: ohne Gebühr, ohne Zugang (AeA-57). */
  readonly erklaerung: Erklaerungsangaben | null;
  readonly teil: Platzauswahl | undefined;
  readonly vorschau: Stornoposten | undefined;
};

/** AUF-15: Zeilen mit eigenem Wert; Text, Zwischensumme und Rabatt fallen mit ihnen. */
const STORNIERBAR: readonly Positionsart[] = ['artikel', 'freitext'];

/**
 * DER TEILSTORNO EINER ZEILE (AUF-15, BUC-51, C3.4) — wie im Service.
 *
 * Zugang und Weg der Erklärung, die Plätze und auf Wunsch die Teilnehmer,
 * dann der Grund. Die Gebühr rechnet der Server bei jeder Änderung neu; bis
 * sie dasteht, bleibt der Knopf gesperrt, und scheitert die Rechnung, steht
 * die Meldung da — der Knopf ohne Zahl hieße stornieren, ohne zu wissen, was
 * es kostet (BUC-45).
 */
function Teilstornoformular({
  auftrag,
  position,
  nummer,
  entwurf,
  beiAbbrechen,
  beiStornieren,
}: {
  readonly auftrag: string;
  readonly position: Auftragsposition;
  readonly nummer: number;
  readonly entwurf: boolean;
  readonly beiAbbrechen: () => void;
  readonly beiStornieren: (eingabe: Teilstorno) => Promise<void>;
}): JSX.Element {
  /* CRM-103: die Eingabe, die hinter der offenen Rückfrage wartet. */
  const [frage, setzeFrage] = useState<Teilstorno | undefined>(undefined);
  const [grund, setzeGrund] = useState('');
  const [erklaerung, setzeErklaerung] = useState<Erklaerungsangaben>(neueErklaerung);
  const [stand, setzeStand] = useState<Positionsteilnehmer | undefined>(undefined);
  const [plaetze, setzePlaetze] = useState('');
  const [auswahl, setzeAuswahl] = useState<readonly string[]>([]);
  const [vorschau, setzeVorschau] = useState<Stornoposten | undefined>(undefined);
  const [vorschaufehler, setzeVorschaufehler] = useState<ApiFehler | undefined>(undefined);

  /* C3.4: Plätze und Teilnehmer der Buchung an dieser Zeile — nur, wenn es eine gibt. */
  useEffect(() => {
    let gilt = true;
    void holePositionsteilnehmer(auftrag, position.id)
      .then((t) => {
        if (!gilt) return;
        setzeStand(t);
        setzePlaetze(String(t.plaetze));
      })
      .catch(() => {
        // Ohne Auswahl geht die ganze Zeile — wie bisher; die Vorschau sagt, was es kostet.
        if (gilt) setzeStand(undefined);
      });
    return () => {
      gilt = false;
    };
  }, [auftrag, position.id]);

  const gesamt = stand?.buchung === null || stand === undefined ? 0 : stand.plaetze;
  const anzahl = Number(plaetze);
  const platzzahlGilt = gesamt === 0 || (Number.isInteger(anzahl) && anzahl >= 1 && anzahl <= gesamt);
  const teil: Platzauswahl | undefined =
    gesamt > 1 && platzzahlGilt && anzahl < gesamt
      ? { plaetze: anzahl, ...(auswahl.length > 0 ? { teilnehmer: auswahl } : {}) }
      : undefined;
  const zugang = entwurf ? undefined : erklaerung.zugang;
  const teilSchluessel = teil === undefined ? '' : `${String(teil.plaetze)}:${auswahl.join(',')}`;

  /*
   * C7.5, BUC-45: die Gebühr zu genau dieser Eingabe. Jede Änderung verwirft
   * die alte Zahl, bevor die neue kommt — sonst stünde kurz die Gebühr des
   * vorigen Zugangs neben dem Knopf.
   */
  useEffect(() => {
    setzeVorschau(undefined);
    setzeVorschaufehler(undefined);
    if (!platzzahlGilt || !auswahlStimmt(plaetze, auswahl) || zugang === '') return;
    let gilt = true;
    void holePositionsvorschau(auftrag, position.id, zugang, teil)
      .then((v) => {
        if (gilt) setzeVorschau(v);
      })
      .catch((ursache: unknown) => {
        if (gilt) setzeVorschaufehler(ursache as ApiFehler);
      });
    return () => {
      gilt = false;
    };
    // `teil` folgt aus `teilSchluessel`; als Objekt wäre es bei jedem Bild neu.
  }, [auftrag, position.id, zugang, teilSchluessel, platzzahlGilt]);

  const fertig =
    vorschau !== undefined && grund.trim() !== '' && platzzahlGilt && auswahlStimmt(plaetze, auswahl);

  return (
    <section className="karte karte--zweit" aria-labelledby={`teilstorno-${position.id}-titel`}>
      <h3 id={`teilstorno-${position.id}-titel`}>Position {nummer} stornieren</h3>
      {entwurf ? (
        <p className="feld__hilfe">
          Der Auftrag ist ein Entwurf: Die Zeile fällt ohne Gebühr weg.
        </p>
      ) : (
        <Erklaerungsfelder
          id={`teilstorno-${position.id}`}
          wert={erklaerung}
          beiAenderung={setzeErklaerung}
        />
      )}

      {gesamt > 1 && stand !== undefined && (
        <Platzauswahlfelder
          id={`teilstorno-${position.id}`}
          gesamt={gesamt}
          plaetze={plaetze}
          beiPlaetze={(wert) => {
            setzePlaetze(wert);
            setzeAuswahl([]);
          }}
          teilnehmer={stand.teilnehmer}
          auswahl={auswahl}
          beiAuswahl={setzeAuswahl}
        />
      )}
      {!platzzahlGilt && (
        <p className="feld__fehler-satz" role="alert">
          Zwischen 1 und {gesamt} Plätzen.
        </p>
      )}

      {vorschaufehler !== undefined && <Meldung fehler={vorschaufehler} />}
      {vorschau !== undefined && (
        <p className="hinweiskarte" role="status">
          Stornogebühr {alsEuro(vorschau.gebuehr)} ({satzText(vorschau)}
          {vorschau.terminbeginn !== null && `, Termin ${String(alsZeitpunkt(vorschau.terminbeginn))}`}
          ) · zu zahlen {alsEuro(vorschau.zuBerechnen)} · zu erstatten {alsEuro(vorschau.zuErstatten)}
          {vorschau.hinweise.length > 0 && <> — {vorschau.hinweise.join(' ')}</>}
        </p>
      )}

      <Stornogrundfeld
        id={`teilstorno-${position.id}-grund`}
        beschriftung="Grund des Teilstornos"
        wert={grund}
        beiAenderung={setzeGrund}
        hilfe={
          teil === undefined
            ? 'Die Zeile bleibt stehen und zählt nicht mehr mit. Der Grund erklärt in der Fakturierungsliste, warum sie dort fehlt.'
            : 'Die Zeile bleibt wirksam, mit weniger Plätzen. Der Grund steht am Stornovorgang.'
        }
      />

      <div className="knopfreihe knopfreihe--abschluss">
        <button type="button" className="knopf" onClick={beiAbbrechen}>
          Abbrechen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={!fertig}
          onClick={() =>
            setzeFrage({
              grund: grund.trim(),
              erklaerung: entwurf ? null : erklaerung,
              teil,
              vorschau,
            })
          }
        >
          {teil === undefined ? 'Position stornieren …' : `${String(teil.plaetze)} von ${String(gesamt)} Plätzen stornieren …`}
        </button>
      </div>

      {frage !== undefined && (
        <Rueckfrage
          frage={
            frage.teil === undefined
              ? `Position ${String(nummer)} stornieren?`
              : `${String(frage.teil.plaetze)} von ${String(gesamt)} Plätzen stornieren?`
          }
          unterzeile={position.bezeichnung}
          tat="Stornieren"
          beiSchliessen={() => setzeFrage(undefined)}
          beiBestaetigen={async () => {
            await beiStornieren(frage);
            setzeFrage(undefined);
          }}
        >
          {/* BUC-45: die Zahlen der Vorschau, die über dem Knopf stand. */}
          {frage.vorschau !== undefined && (
            <dl className="angaben">
              <dt>Gebühr</dt>
              <dd>
                {alsEuro(frage.vorschau.gebuehr)} ({satzText(frage.vorschau)})
              </dd>
              <dt>Zu zahlen</dt>
              <dd>{alsEuro(frage.vorschau.zuBerechnen)}</dd>
              <dt>Zu erstatten</dt>
              <dd>{alsEuro(frage.vorschau.zuErstatten)}</dd>
            </dl>
          )}
          <p>
            {frage.teil === undefined
              ? 'Die Zeile bleibt stehen und zählt nicht mehr mit.'
              : `Die Zeile bleibt wirksam, mit ${String(gesamt - frage.teil.plaetze)} statt ${String(gesamt)} Plätzen.`}
            {frage.vorschau?.mitBuchung === true && ' Die stornierten Plätze werden frei.'}
          </p>
          <p>Grund: „{frage.grund}“</p>
          {/* BUC-148 gilt dem Vertrag; am Entwurf sagt die Rückfrage dazu nichts zu. */}
          {!entwurf && <p className="feld__hilfe">{RUECKNEHMBAR}</p>}
        </Rueckfrage>
      )}
    </section>
  );
}

/** L5 (CRM-116): Termin und, wenn gewünscht, Frist der Option. */
type Terminwahl = Parameters<typeof positionTerminSetzen>[2];

/**
 * L5 (CRM-116), AUF-17: den Termin einer Einzelplatzzeile im Entwurf wählen
 * oder wechseln. Die alte Option gibt der Server frei; die neue prüft er wie
 * eine Neubuchung. Ohne neue Frist gilt die bisherige weiter.
 */
function Terminwahlfenster({
  position,
  artikel,
  kunde,
  kundenart,
  beiSchliessen,
  beiUebernahme,
}: {
  readonly position: Auftragsposition;
  readonly artikel: string;
  readonly kunde: string;
  readonly kundenart: string;
  readonly beiSchliessen: () => void;
  readonly beiUebernahme: (eingabe: Terminwahl) => Promise<void>;
}): JSX.Element {
  const [termin, setzeTermin] = useState(position.bezugId ?? '');
  const [optionsfrist, setzeOptionsfrist] = useState('');
  const [laeuft, setzeLaeuft] = useState(false);
  const unveraendert = termin === (position.bezugId ?? '') && optionsfrist === '';

  return (
    <Dialog
      titel={position.bezugId === null ? 'Termin wählen' : 'Termin ändern'}
      unterzeile={position.bezeichnung}
      beiSchliessen={beiSchliessen}
    >
      <Platzterminwahl
        id={`termin-${position.id}`}
        artikel={artikel}
        kunde={kunde}
        kundenart={kundenart}
        menge={Math.ceil(Number(position.menge ?? '1'))}
        wert={termin}
        beiWahl={setzeTermin}
      />
      {termin !== '' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor={`frist-${position.id}`}>
            Plätze zurücklegen bis
          </label>
          <input
            id={`frist-${position.id}`}
            type="date"
            value={optionsfrist}
            onChange={(e) => setzeOptionsfrist(e.target.value)}
          />
          {/* BUC-76, KAP-09 */}
          <p className="feld__hilfe">
            {optionsfrist === ''
              ? 'Leer heißt: Eine bestehende Option zieht mit ihrer Frist mit, sonst wird '
                + 'nichts zurückgelegt.'
              : 'Die Plätze sind bis zum Ende dieses Tages für diesen Kunden gehalten.'}
          </p>
        </div>
      )}
      {/* CRM-82, CRM-87: Abbruch vorn, Hauptknopf zuletzt. */}
      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={beiSchliessen}>
          Abbrechen
        </button>
        <button
          type="button"
          className="knopf knopf--haupt"
          disabled={unveraendert || laeuft}
          onClick={() => {
            setzeLaeuft(true);
            void beiUebernahme({
              termin: termin === '' ? null : termin,
              ...(termin !== '' && optionsfrist !== '' ? { optionsfrist } : {}),
            }).finally(() => setzeLaeuft(false));
          }}
        >
          Übernehmen
        </button>
      </div>
    </Dialog>
  );
}

function NeuePosition({
  termine,
  terminfehler,
  kunde,
  kundenart,
  beiAnlegen,
}: {
  readonly termine: readonly Exklusivtermin[];
  /** ERR-01: „keiner vereinbart" und „nicht erreichbar" sind zweierlei. */
  readonly terminfehler: boolean;
  /** KAP-04: Die freien Plätze rechnen aus der Sicht des Auftragskunden. */
  readonly kunde: string;
  /** BUC-105: nur Termine, die für die Kundenart des Auftrags buchbar sind. */
  readonly kundenart: string;
  readonly beiAnlegen: (eingabe: Parameters<typeof positionAnlegen>[1]) => void;
}): JSX.Element {
  const [art, setzeArt] = useState<Positionsart>('artikel');
  const [artikel, setzeArtikel] = useState<Artikelzeile | undefined>(undefined);
  /* Die Auswahl liegt seit dem 06.09.2026 in einem Fenster, nicht unter dem Feld. */
  const [wahlOffen, setzeWahlOffen] = useState(false);
  const [form, setzeForm] = useState<Vertriebsform | undefined>(undefined);
  const [termin, setzeTermin] = useState('');
  /* AUF-05: die vier Bezugsarten. Vorbelegt nach der Form (siehe unten). */
  const [bezugsartWahl, setzeBezugsart] = useState<Bezugsart | undefined>(undefined);
  const [von, setzeVon] = useState('');
  const [bis, setzeBis] = useState('');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [menge, setzeMenge] = useState('1');
  const [einzelpreis, setzeEinzelpreis] = useState('');
  const [steuersatz, setzeSteuersatz] = useState('DE-19');

  /* PRD-42: die Wahl nur, wo das Produkt mehr als eine Form fuehrt. */
  const formen = artikel?.vertriebsformen ?? [];
  const gewaehlteForm: Vertriebsform | undefined = form ?? formen[0];
  const zurWahl = formen.length > 1;
  /*
   * Die VORBELEGUNG folgt der Verkaufsform, weil AUF-05 sie dort hat: Ein
   * Platz und eine Pauschale hängen am Termin, eine Ressourcenüberlassung an
   * einem Zeitraum, eine Zusatzleistung je Teilnehmer am Teilnehmer. Wer es
   * anders braucht, wählt — die Vorbelegung ist eine Abkürzung, keine Regel.
   */
  const vorbelegteBezugsart: Bezugsart =
    gewaehlteForm === 'ressource'
      ? 'zeitraum'
      : gewaehlteForm === 'zusatzleistung'
        ? 'teilnehmer'
        : 'termin';
  const bezugsart: Bezugsart = bezugsartWahl ?? vorbelegteBezugsart;

  /* BUC-76: bis wann die Plätze zurückgelegt werden. Leer heißt: gar nicht. */
  const [optionsfrist, setzeOptionsfrist] = useState('');
  const passende = termine.filter((t) => t.artikel === artikel?.id);
  const gewaehlterTermin = passende.find((t) => t.id === termin);
  const pauschal = gewaehlterTermin?.pauschale != null;

  const brauchtBetrag = art === 'artikel' || art === 'freitext' || art === 'rabatt';
  const bereit =
    art === 'artikel'
      ? artikel !== undefined && menge !== ''
      : brauchtBetrag
        ? bezeichnung !== '' && menge !== '' && einzelpreis !== ''
        : bezeichnung !== '';

  function anlegen(): void {
    // Dasselbe wie im Angebot: Der Abzug wird HIER negiert, nicht getippt.
    const preis =
      art === 'rabatt' && einzelpreis !== '' && !einzelpreis.startsWith('-')
        ? `-${einzelpreis}`
        : einzelpreis;

    beiAnlegen({
      art,
      ...(art === 'artikel' ? { artikel: artikel?.id ?? null } : {}),
      ...(art === 'artikel' && zurWahl && gewaehlteForm !== undefined
        ? { vertriebsform: gewaehlteForm }
        : {}),
      ...(bezeichnung === '' ? {} : { bezeichnung }),
      /*
       * PRD-46: Eine Pauschalposition trägt genau einen Termin. Die
       * Teilnehmerzahl wirkt auf Planung und Ressourcenbedarf, nicht auf den
       * Preis — eine Abrechnung nach Köpfen ist bei Exklusivterminen nicht
       * vorgesehen (E-49).
       */
      ...(brauchtBetrag ? { menge: pauschal ? 1 : Number(menge) } : {}),
      ...(preis === '' ? {} : { einzelpreis: preis }),
      ...(art === 'rabatt' ? { steuerschluessel: steuersatz, steuerProzent: null } : {}),
      /*
       * AUF-05: nur die Artikelzeile trägt einen Bezug; Freitext, Text,
       * Zwischensumme und Rabattzeile fallen unter die Ausnahme.
       *
       * DIE KENNUNG GEHÖRT DAZU. Bis hierher stand nur die Art da und die
       * Kennung blieb leer — ein Terminbezug ohne Termin. Er sagte nichts
       * aus und sah aus, als sage er etwas; die Preisfindung konnte weder
       * den Standort (AA-011) noch die vereinbarte Pauschale (DAT-08) sehen.
       */
      /*
       * AUF-05: WORAUF SICH DIE LEISTUNG BEZIEHT.
       *
       * Hier stand für JEDE Artikelzeile fest 'termin', und die drei anderen
       * Arten waren in der Maske nicht erfassbar. Bei einer
       * Ressourcenüberlassung nennt AUF-05 den Zeitraum, bei einer
       * Zusatzleistung je Teilnehmer den Teilnehmer — „Termin offen" an einer
       * Verpflegungszeile ist keine Aussage, sondern ein Platzhalter, der wie
       * eine aussieht.
       */
      bezugsart: art === 'artikel' ? bezugsart : null,
      ...(art === 'artikel' && bezugsart === 'termin' && termin !== ''
        ? { bezugId: termin }
        : {}),
      ...(art === 'artikel' && bezugsart === 'zeitraum'
        ? { bezugVon: von === '' ? null : von, bezugBis: bis === '' ? null : bis }
        : {}),
      /*
       * BUC-76: die Frist der Option. Sie geht nur mit, wenn eine steht —
       * wer keine nennt, will keine Option, und eine erfundene Vorgabefrist
       * wäre eine Zusage, die niemand gegeben hat.
       */
      ...(art === 'artikel' && bezugsart === 'termin' && termin !== '' && optionsfrist !== ''
        ? { optionsfrist }
        : {}),
    });
    setzeArtikel(undefined);
    setzeForm(undefined);
    setzeTermin('');
    setzeBezugsart(undefined);
    setzeVon('');
    setzeBis('');
    setzeOptionsfrist('');
    setzeBezeichnung('');
    setzeMenge('1');
    setzeEinzelpreis('');
  }

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Position hinzufügen</h2>
        {/* AUF-03, AUF-05 */}
      </div>

      <div className="feld">
        <label className="feld__beschriftung" htmlFor="neu-art">
          Art
        </label>
        <select
          id="neu-art"
          value={art}
          onChange={(e) => setzeArt(e.target.value as Positionsart)}
        >
          {(Object.keys(ARTNAME) as Positionsart[]).map((a) => (
            <option key={a} value={a}>
              {ARTNAME[a]}
            </option>
          ))}
        </select>
      </div>

      {/*
        DIE AUSWAHL LIEGT IN EINEM FENSTER, NICHT UNTER DEM FELD — wortgleich
        mit der Angebotsmaske. Eine aufklappende Liste schob die Felder
        darunter weg und zeigte nur acht Zeilen ohne Vertriebsform.
      */}
      {art === 'artikel' && (
        <div className="feld">
          <span className="feld__beschriftung" id="neu-artikel-beschriftung">
            Artikel
          </span>
          {artikel === undefined ? (
            <button
              type="button"
              className="knopf"
              aria-describedby="neu-artikel-beschriftung"
              onClick={() => setzeWahlOffen(true)}
            >
              Artikel wählen …
            </button>
          ) : (
            <p className="kanalliste__wert">
              {artikel.artikelnummer} · {artikel.bezeichnung}{' '}
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => setzeWahlOffen(true)}
              >
                Anderen wählen
              </button>
            </p>
          )}
        </div>
      )}

      {wahlOffen && (
        <Artikelwahl
          beiSchliessen={() => setzeWahlOffen(false)}
          beiWahl={(gewaehlt) => {
            setzeArtikel(gewaehlt);
            setzeForm(undefined);
            setzeTermin('');
            setzeWahlOffen(false);
          }}
        />
      )}

      {/* PRD-42: die Wahl nur, wo das Produkt mehr als eine Form führt. */}
      {art === 'artikel' && artikel !== undefined && zurWahl && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-form">
            Verkaufsform
          </label>
          <select
            id="neu-form"
            value={gewaehlteForm ?? ''}
            onChange={(e) => {
              setzeForm(e.target.value as Vertriebsform);
              // Ein Exklusivtermin ist kein Platztermin und umgekehrt (0110).
              setzeTermin('');
              setzeOptionsfrist('');
              setzeTermin('');
            }}
          >
            {formen.map((f) => (
              <option key={f} value={f}>
                {VERTRIEBSFORMNAME[f]}
              </option>
            ))}
          </select>
          <p className="feld__hilfe">
            Jede Form hat ihren eigenen Preis. Von ihr hängt außerdem ab, wann
            fakturiert wird.
          </p>
        </div>
      )}

      {/*
        AUF-05: der Gegenstand der Position.

        NUR BEIM EXKLUSIVVERKAUF. Die Auskunft `exklusive()` liefert nach
        BUC-67 ausschließlich Exklusivtermine; angeboten wurde sie aber bei
        jeder Form. Wer einen Einzelplatz erfasste, bekam damit nur
        Exklusivtermine zur Wahl — und band einen Teilnehmerplatz an einen
        Termin, dessen Kapazität nach BUC-68 einem Kunden vollständig gehört.
        Seit Migration 0110 weist die Ablage das ab; die Maske bietet es gar
        nicht erst an. Die Angebotsmaske gatet seit jeher richtig.
      */}
      {/* AUF-05: die Bezugsart als Wahl, nicht als Annahme. */}
      {art === 'artikel' && artikel !== undefined && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-bezugsart">
            Leistungsbezug
          </label>
          <select
            id="neu-bezugsart"
            value={bezugsart}
            onChange={(e) => setzeBezugsart(e.target.value as Bezugsart)}
          >
            {(['termin', 'teilnehmer', 'ressource', 'zeitraum'] as const).map((b) => (
              <option key={b} value={b}>
                {BEZUGSNAME[b]}
              </option>
            ))}
          </select>
          <p className="feld__hilfe">
            Worauf sich die Leistung bezieht. Ohne ihn entstünde keine Verbindung
            zu Tagesplan und Durchführung.
          </p>
        </div>
      )}

      {art === 'artikel' && artikel !== undefined && bezugsart === 'zeitraum' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-von">
            Zeitraum
          </label>
          <input
            id="neu-von"
            type="date"
            value={von}
            onChange={(e) => setzeVon(e.target.value)}
            aria-label="Zeitraum von"
          />
          <input
            type="date"
            value={bis}
            onChange={(e) => setzeBis(e.target.value)}
            aria-label="Zeitraum bis"
          />
          {/* ANG-25 */}
          <p className="feld__hilfe">
            Eine Ressourcenüberlassung wird über einen Zeitraum verkauft.
            Das Anfangsdatum ist Pflicht.
          </p>
        </div>
      )}

      {art === 'artikel' && artikel !== undefined && bezugsart === 'termin'
        && gewaehlteForm === 'exklusivtermin' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-termin">
            Exklusivtermin
          </label>
          <select id="neu-termin" value={termin} onChange={(e) => setzeTermin(e.target.value)}>
            <option value="">— noch offen</option>
            {passende.map((t) => (
              <option key={t.id} value={t.id}>
                {terminname(t.id, passende)}
              </option>
            ))}
          </select>
          <p className="feld__hilfe">
            {pauschal
              ? 'Für diesen Termin gilt die vereinbarte Pauschale und nicht die Preisliste. '
                + 'Die Menge ist eins — der Rechnungsbetrag hängt am '
                + 'vereinbarten Umfang und nicht an der Teilnehmerzahl.'
              : terminfehler
                ? 'Die Terminauskunft antwortet nicht. Ob Exklusivtermine vereinbart sind, '
                  + 'lässt sich gerade nicht sagen — ohne Termin gilt der Listenpreis.'
                : passende.length === 0
                  ? 'Für dieses Produkt ist mit diesem Kunden kein Exklusivtermin vereinbart. '
                    + 'Ohne Termin bleibt der Bezug offen und es gilt der Listenpreis.'
                  : 'Steht der Termin, rechnet die Preisfindung mit seinem Standort '
                    + 'und, wenn eine Pauschale vereinbart ist, mit ihr.'}
          </p>
        </div>
      )}

      {/*
        L5 (CRM-116): DER OFFENE TERMIN DES EINZELPLATZES — wie im Angebot
        (ANG-29) an der Zeile gewählt. Gebucht wird mit der Bestätigung
        (BUC-75), gehalten mit der Option darunter (BUC-76).
      */}
      {art === 'artikel' && artikel !== undefined && bezugsart === 'termin'
        && gewaehlteForm === 'einzelplatz' && (
        <Platzterminwahl
          id="neu-platztermin"
          artikel={artikel.id}
          kunde={kunde}
          kundenart={kundenart}
          menge={Number(menge === '' ? '0' : menge)}
          wert={termin}
          beiWahl={(t) => {
            setzeTermin(t);
            if (t === '') setzeOptionsfrist('');
          }}
        />
      )}

      {/*
        BUC-76: DIE BEFRISTETE OPTION.

        "Telefonisch vorgemerkte Plätze werden als befristete Option nach
        KAP-02 gehalten, mit frei setzbarer Frist, Zuständigem und
        automatischem Verfall."

        Nur beim EINZELPLATZ auf einem offenen Termin: Ein Exklusivtermin
        gehört dem Kunden ganz, dort gibt es keine Plätze zurückzulegen.
      */}
      {art === 'artikel' && artikel !== undefined && bezugsart === 'termin'
        && gewaehlteForm === 'einzelplatz' && termin !== '' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-optionsfrist">
            Plätze zurücklegen bis
          </label>
          <input
            id="neu-optionsfrist"
            type="date"
            value={optionsfrist}
            onChange={(e) => setzeOptionsfrist(e.target.value)}
          />
          {/* KAP-08 */}
          {/* KAP-09 */}
          <p className="feld__hilfe">
            {optionsfrist === ''
              ? 'Leer heißt: Es wird nichts zurückgelegt. Die Plätze kommen erst mit der '
                + 'Bestätigung — bis dahin kann sie jemand anders buchen.'
              : 'Die Plätze sind bis zum Ende dieses Tages für diesen Kunden gehalten und '
                + 'zählen ab sofort nicht mehr als frei. Danach verfallen sie von '
                + 'selbst. Mit der Bestätigung wird die Option zur Buchung.'}
          </p>
        </div>
      )}

      {art !== 'artikel' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-bezeichnung">
            Bezeichnung
          </label>
          <input
            id="neu-bezeichnung"
            type="text"
            value={bezeichnung}
            onChange={(e) => setzeBezeichnung(e.target.value)}
          />
        </div>
      )}

      {brauchtBetrag && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-menge">
            Menge
          </label>
          <input
            id="neu-menge"
            type="number"
            min="0"
            step="1"
            value={pauschal ? '1' : menge}
            disabled={pauschal}
            onChange={(e) => setzeMenge(e.target.value)}
          />
        </div>
      )}

      {brauchtBetrag && art !== 'artikel' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-preis">
            {art === 'rabatt' ? 'Abzug' : 'Einzelpreis'}
          </label>
          <input
            id="neu-preis"
            type="text"
            value={einzelpreis}
            onChange={(e) => setzeEinzelpreis(e.target.value)}
            placeholder="249.00"
          />
          {art === 'rabatt' && (
            <p className="feld__hilfe">
              Ohne Minus eingeben — der Abzug wird beim Senden negiert. Der Steuersatz ist
              Pflicht: Der Abzug mindert die Bemessungsgrundlage genau einer Satzgruppe.
            </p>
          )}
        </div>
      )}

      {art === 'rabatt' && (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="neu-steuer">
            Steuerschlüssel
          </label>
          <input
            id="neu-steuer"
            type="text"
            value={steuersatz}
            onChange={(e) => setzeSteuersatz(e.target.value)}
          />
        </div>
      )}

      <div className="knopfreihe">
        <button type="button" className="knopf knopf--haupt" disabled={!bereit} onClick={anlegen}>
          Hinzufügen
        </button>
      </div>
    </div>
  );
}

function Kopfdaten({
  auftrag,
  zurKunde,
  bearbeitbar,
  beiSichern,
}: {
  readonly auftrag: Auftrag;
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde: ((id: string) => void) | undefined;
  readonly bearbeitbar: boolean;
  readonly beiSichern: (feld: string, wert: string | null) => void;
}): JSX.Element {
  return (
    <div className="spalten spalten--zweidrittel">
      <div className="karte">
        <div className="karte__titel">
          <h2>Kopfdaten</h2>
          {/* AUF-02 */}
        </div>
        <Feld
          name="betreff"
          beschriftung="Betreff"
          wert={auftrag.betreff}
          bearbeitbar={bearbeitbar}
          beiSichern={beiSichern}
        />
        <Feld
          name="bestellreferenz"
          beschriftung="Bestellreferenz des Kunden"
          wert={auftrag.bestellreferenz ?? ''}
          bearbeitbar={bearbeitbar}
          beiSichern={beiSichern}
        />
        <Feld
          name="kopftext"
          beschriftung="Kopftext"
          wert={auftrag.kopftext ?? ''}
          mehrzeilig
          bearbeitbar={bearbeitbar}
          beiSichern={beiSichern}
        />
        <Feld
          name="fusstext"
          beschriftung="Fußtext"
          wert={auftrag.fusstext ?? ''}
          mehrzeilig
          bearbeitbar={bearbeitbar}
          beiSichern={beiSichern}
        />
      </div>

      <aside className="karte" aria-label="Vertrag und Fakturierung">
        <div className="karte__titel">
          <h2>Vertrag</h2>
          {/* AUF-02, AUF-21 */}
        </div>
        <dl className="angaben">
          <dt>Kunde</dt>
          <dd>
            <KundeVerweis kundeId={auftrag.kunde} zurAkte={zurKunde}>
              {auftrag.kundenname}
            </KundeVerweis>
          </dd>
          {auftrag.bestellerName !== null && (
            <>
              <dt>Besteller</dt>
              <dd>
                <KundeVerweis kundeId={auftrag.kunde} ansprechpartnerId={auftrag.besteller} zurAkte={zurKunde}>
                  {auftrag.bestellerName}
                </KundeVerweis>
              </dd>
            </>
          )}
          {auftrag.rechnungsempfaengerName !== null && (
            <>
              <dt>Rechnungsempfänger</dt>
              <dd>
                <KundeVerweis kundeId={auftrag.rechnungsempfaenger} zurAkte={zurKunde}>
                  {auftrag.rechnungsempfaengerName}
                </KundeVerweis>
              </dd>
            </>
          )}
          <dt>Belegdatum</dt>
          <dd>{auftrag.belegdatum}</dd>
          <dt>Zahlungsziel</dt>
          <dd>
            {auftrag.zahlungsziel} Tage
            {auftrag.skontoProzent !== null
              && ` · ${auftrag.skontoProzent} % Skonto binnen ${String(auftrag.skontoTage)} Tagen`}
          </dd>
          <dt>Fakturierung</dt>
          <dd>
            {ZEITPUNKTNAME[auftrag.fakturierungszeitpunkt]}
            {auftrag.fakturierungsdatum !== null && ` (${auftrag.fakturierungsdatum})`}
          </dd>
          <dt>Preisführung</dt>
          <dd>{auftrag.preisfuehrung === 'brutto' ? 'brutto' : 'netto'}</dd>
          {/*
            ANG-43: aus dem Angebot uebernommen oder bei der Erfassung gesetzt.
            Sie bestimmt nach BUC-45, was ein Ruecktritt kostet — und ohne sie
            findet der Storno gar keine Staffel.
          */}
          <dt>Vertragsart</dt>
          <dd>
            {VERTRAGSARTNAME[auftrag.vertragsart]}
            <span className="kanalliste__name"> · bestimmt die Stornostaffel</span>
          </dd>
          {/*
            PAR-03: Das Vermittlerkennzeichen steht in der Akte, nicht nur in
            der Datenbank. Wer den Beleg ansieht, muss sehen, wem die
            Vermittlung zugerechnet wird — sie entscheidet über die Auswertung
            nach PAR-01 und ist nachträglich nur mit Grund änderbar.
          */}
          {auftrag.vermittler !== null && (
            <>
              <dt>Vermittler</dt>
              <dd>
                {auftrag.vermittlername ?? '—'}
                {auftrag.vermittlercode !== null && (
                  <span className="kanalliste__name"> · {auftrag.vermittlercode}</span>
                )}
              </dd>
            </>
          )}
          <dt>Bearbeiter</dt>
          <dd>{auftrag.bearbeiterName ?? '—'}</dd>
        </dl>
        <p className="feld__hilfe">
          Zahlungsziel und Skonto sind beim Anlegen aus der Kundenkondition übernommen und im
          Auftrag eingefroren: Eine spätere Änderung der Kondition wirkt nicht
          rückwirkend, sonst liefe eine Mahnung gegen eine Vereinbarung, die es damals nicht gab.
        </p>
      </aside>
    </div>
  );
}

function Feld({
  name,
  beschriftung,
  wert,
  mehrzeilig,
  bearbeitbar,
  beiSichern,
}: {
  readonly name: string;
  readonly beschriftung: string;
  readonly wert: string;
  readonly mehrzeilig?: boolean;
  readonly bearbeitbar: boolean;
  readonly beiSichern: (feld: string, wert: string | null) => void;
}): JSX.Element {
  const [entwurf, setzeEntwurf] = useState(wert);
  useEffect(() => setzeEntwurf(wert), [wert]);

  const sichern = (): void => {
    if (entwurf === wert) return;
    beiSichern(name, entwurf === '' ? null : entwurf);
  };

  return (
    <div className="feld">
      <label className="feld__beschriftung" htmlFor={`auftrag-feld-${name}`}>
        {beschriftung}
      </label>
      {mehrzeilig === true ? (
        <textarea
          id={`auftrag-feld-${name}`}
          value={entwurf}
          rows={4}
          disabled={!bearbeitbar}
          onChange={(e) => setzeEntwurf(e.target.value)}
          onBlur={sichern}
        />
      ) : (
        <input
          id={`auftrag-feld-${name}`}
          type="text"
          value={entwurf}
          disabled={!bearbeitbar}
          onChange={(e) => setzeEntwurf(e.target.value)}
          onBlur={sichern}
        />
      )}
    </div>
  );
}

/** Der Summenblock — je Steuersatzgruppe, wie am Angebot (RND-06). */
function Summen({ auftrag }: { readonly auftrag: Auftrag }): JSX.Element {
  const s = auftrag.summen;
  const berechnet = auftrag.positionen.reduce(
    (bisher, p) => bisher + Number(p.berechneterWert),
    0,
  );

  return (
    <div className="spalten spalten--zweidrittel">
      <div className="karte">
        <div className="karte__titel">
          <h2>Summen</h2>
          {/* AUF-03, RND-05, RND-07 */}
        </div>
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th scope="col">Steuersatz</th>
                <th scope="col" className="zahl">
                  Netto
                </th>
                <th scope="col" className="zahl">
                  Steuer
                </th>
                <th scope="col" className="zahl">
                  Brutto
                </th>
              </tr>
            </thead>
            <tbody>
              {s.gruppen.map((g) => (
                <tr key={g.schluessel}>
                  <td>
                    {g.schluessel} · {alsProzent(g.prozent)}
                    {g.hinweis !== null && <span className="kanalliste__name"> · {g.hinweis}</span>}
                  </td>
                  <td className="zahl">{alsEuro(g.netto)}</td>
                  <td className="zahl">{alsEuro(g.steuer)}</td>
                  <td className="zahl">{alsEuro(g.brutto)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Gesamt</th>
                <td className="zahl">{alsEuro(s.belegnetto)}</td>
                <td className="zahl">{alsEuro(s.steuerSumme)}</td>
                <td className="zahl">
                  <strong>{alsEuro(s.belegbrutto)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {s.gruppen.length === 0 && <p className="feld__hilfe">Noch keine berechnete Position.</p>}

        <Rundungshinweis faelle={s.nichtDarstellbar} />
      </div>

      <aside className="karte" aria-label="Fakturierung">
        <div className="karte__titel">
          <h2>Fakturierung</h2>
          {/* AUF-20 */}
        </div>
        <dl className="angaben">
          <dt>Stand</dt>
          <dd>{FAKTURIERUNGSNAME[auftrag.fakturierung]}</dd>
          <dt>Bereits berechnet</dt>
          <dd>{alsEuro(berechnet.toFixed(2))}</dd>
          <dt>Stornierte Positionen</dt>
          <dd>{auftrag.positionen.filter((p) => p.storniertAm !== null).length}</dd>
        </dl>
        <p className="feld__hilfe">
          „Bereits berechnet“ ist die Summe der an den Positionen vermerkten Werte und
          nicht die Belegsumme: Eine Anzahlung wird später einen Wert ohne Menge abrechnen. Der
          Betrag entsteht mit der Rechnung.
        </p>
        {s.nichtGewertet !== '0.00' && (
          <p className="feld__hilfe">
            Stornierte Positionen zusammen: {alsEuro(s.nichtGewertet)}. Sie zählen nicht in die Summe
 und bleiben mit ihrem Grund stehen.
          </p>
        )}
      </aside>
    </div>
  );
}

/**
 * Anzahlungen und Abschläge (AUF-22).
 *
 * DER PROZENTSATZ IST DIE ERSTE WAHL, und die Maske sagt warum: „30 % bei
 * Auftragserteilung" macht eine spätere Mengenänderung mit, ein fester Betrag
 * nicht. Beides zugleich lässt der Server nicht zu — es wären zwei Zahlen für
 * denselben Anspruch.
 */
function Anzahlungen({
  auftrag,
  bearbeitbar,
  beiAnlegen,
  beiLoeschen,
  darfEingang,
  beiEingang,
  beiBerichtigen,
}: {
  readonly auftrag: Auftrag;
  readonly bearbeitbar: boolean;
  readonly beiAnlegen: (eingabe: Parameters<typeof anzahlungAnlegen>[1]) => void;
  readonly beiLoeschen: (anzahlung: string) => Promise<void>;
  readonly darfEingang: boolean;
  readonly beiEingang: (anzahlung: string, am: string) => Promise<void>;
  /** Das Datum eines schon vermerkten Eingangs — ohne Rückfrage, es bleibt berichtigbar. */
  readonly beiBerichtigen: (anzahlung: string, am: string) => void;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Anzahlung[]>([]);
  /* CRM-103: Entfernen und der erste Eingang fragen nach; das Berichtigen des Datums nicht. */
  const [frage, setzeFrage] = useState<
    | { readonly art: 'entfernen'; readonly zeile: Anzahlung }
    | { readonly art: 'eingang'; readonly zeile: Anzahlung; readonly am: string }
    | undefined
  >(undefined);
  const [eingangAm, setzeEingangAm] = useState(heute());
  const [art, setzeArt] = useState<Anzahlungsart>('anzahlung');
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [nachSatz, setzeNachSatz] = useState(true);
  const [wert, setzeWert] = useState('30.00');
  const [faellig, setzeFaellig] = useState(auftrag.belegdatum);

  useEffect(() => {
    void holeAnzahlungen(auftrag.id)
      .then(setzeZeilen)
      .catch(() => setzeZeilen([]));
  }, [auftrag.id, auftrag.stand]);

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Anzahlungen und Abschläge</h2>
        {/* AUF-22 */}
      </div>

      {/* CRM-106: Die Zeile trägt eine Eingabe — auf dem Telefon steht sie als Karte. */}
      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle tabelle--karten">
          <thead>
            <tr>
              <th scope="col">Art</th>
              <th scope="col">Bezeichnung</th>
              <th scope="col">Vereinbarung</th>
              <th scope="col" className="zahl">
                Betrag
              </th>
              <th scope="col">fällig</th>
              <th scope="col">Stand</th>
              <th scope="col">Handlung</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.id}>
                <td data-spalte="Art">{ANZAHLUNGSNAME[z.art]}</td>
                <td data-spalte="Bezeichnung">{z.bezeichnung}</td>
                <td data-spalte="Vereinbarung">
                  {z.prozentsatz !== null ? (
                    <>
                      {z.prozentsatz} % der Belegsumme
                      {/* AUF-22: nach dem Eingang steht der Betrag fest. */}
                      <span className="kanalliste__name">
                        {z.gezahlterBetrag === null ? ' · folgt der Menge' : ' · beim Eingang festgehalten'}
                      </span>
                    </>
                  ) : (
                    <span className="feld__hilfe">fester Betrag</span>
                  )}
                </td>
                <td className="zahl" data-spalte="Betrag">
                  {alsEuro(z.wirksamerBetrag)}
                  {/* RNG-09: abgesetzt wird, was gezahlt wurde. */}
                  {z.gezahlterBetrag !== null && <span className="feld__hilfe"> gezahlt</span>}
                </td>
                <td className="liste__einzeilig" data-spalte="fällig">{alsTag(z.faelligAm)}</td>
                <td data-spalte="Stand">
                  {/* C7.1: offen → eingegangen → auf der Schlussrechnung abgesetzt. */}
                  {z.abgesetztAm !== null ? (
                    <span className="marke marke--gut">abgesetzt</span>
                  ) : z.bezahltAm !== null ? (
                    <span className="marke">eingegangen am {alsTag(z.bezahltAm)}</span>
                  ) : (
                    <span className="marke marke--inaktiv">offen</span>
                  )}
                </td>
                <td>
                  {darfEingang && z.abgesetztAm === null && (
                    <span className="knopfreihe">
                      <input
                        type="date"
                        aria-label={`Eingang ${z.bezeichnung}`}
                        value={eingangAm}
                        onChange={(e) => setzeEingangAm(e.target.value)}
                      />
                      <button
                        type="button"
                        className="knopf knopf--klein"
                        disabled={eingangAm === ''}
                        onClick={() =>
                          z.bezahltAm === null
                            ? setzeFrage({ art: 'eingang', zeile: z, am: eingangAm })
                            : beiBerichtigen(z.id, eingangAm)
                        }
                      >
                        {z.bezahltAm === null ? 'Eingang vermerken …' : 'Datum berichtigen'}
                      </button>
                    </span>
                  )}
                  {bearbeitbar && z.berechnetAm === null && z.bezahltAm === null && (
                    <button
                      type="button"
                      className="knopf knopf--eingriff knopf--klein"
                      onClick={() => setzeFrage({ art: 'entfernen', zeile: z })}
                    >
                      Entfernen …
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {frage?.art === 'entfernen' && (
        <Rueckfrage
          frage="Anzahlung entfernen?"
          unterzeile={frage.zeile.bezeichnung}
          tat="Entfernen"
          beiSchliessen={() => setzeFrage(undefined)}
          beiBestaetigen={async () => {
            await beiLoeschen(frage.zeile.id);
            setzeFrage(undefined);
          }}
        >
          {/* AUF-22 */}
          <p>
            {frage.zeile.art === 'abschlag' ? 'Der' : 'Die'} {ANZAHLUNGSNAME[frage.zeile.art]} über{' '}
            {alsEuro(frage.zeile.wirksamerBetrag)}, fällig am {alsTag(frage.zeile.faelligAm)}, wird
            gelöscht und lässt sich nicht wiederherstellen, nur neu hinterlegen.
          </p>
        </Rueckfrage>
      )}
      {frage?.art === 'eingang' && (
        <Rueckfrage
          frage="Eingang vermerken?"
          unterzeile={frage.zeile.bezeichnung}
          tat="Vermerken"
          beiSchliessen={() => setzeFrage(undefined)}
          beiBestaetigen={async () => {
            await beiEingang(frage.zeile.id, frage.am);
            setzeFrage(undefined);
          }}
        >
          {/* RNG-09, C7.1 */}
          <p>
            {alsEuro(frage.zeile.wirksamerBetrag)} gelten als am {alsTag(frage.am)} eingegangen.
            Erst damit setzt die Schlussrechnung den Betrag ab. Entfernen lässt sich die Zeile
            danach nicht mehr; das Datum bleibt zu berichtigen.
          </p>
        </Rueckfrage>
      )}

      {zeilen.length === 0 && (
        <p className="feld__hilfe">Noch keine Anzahlung vereinbart.</p>
      )}

      {/* RNG-09 */}
      <p className="feld__hilfe">
        Hinterlegt wird hier. Auf der Schlussrechnung abgesetzt wird eine Anzahlung erst, wenn
        ihr Eingang vermerkt ist — abzugsfähig sind nur vereinnahmte Teilentgelte (§ 14 Abs. 5
        UStG). Mit der Festschreibung steht sie auf „abgesetzt“; ein Storno gibt sie wieder frei.
      </p>

      {bearbeitbar && (
        <>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anz-bezeichnung">
              Bezeichnung
            </label>
            <input
              id="anz-bezeichnung"
              type="text"
              value={bezeichnung}
              onChange={(e) => setzeBezeichnung(e.target.value)}
              placeholder="30 % bei Auftragserteilung"
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anz-art">
              Art
            </label>
            <select
              id="anz-art"
              value={art}
              onChange={(e) => setzeArt(e.target.value as Anzahlungsart)}
            >
              {(Object.keys(ANZAHLUNGSNAME) as Anzahlungsart[]).map((a) => (
                <option key={a} value={a}>
                  {ANZAHLUNGSNAME[a]}
                </option>
              ))}
            </select>
            <p className="feld__hilfe">
              Die Anzahlung geht der Leistung voraus, der Abschlag begleitet sie. Auf der
              Schlussrechnung stehen sie verschieden.
            </p>
          </div>

          <fieldset className="feld">
            <legend className="feld__beschriftung">Wie ist sie vereinbart?</legend>
            <label className="liste__schalter">
              <input type="radio" checked={nachSatz} onChange={() => setzeNachSatz(true)} />
              als Prozentsatz der Belegsumme
            </label>
            <label className="liste__schalter">
              <input type="radio" checked={!nachSatz} onChange={() => setzeNachSatz(false)} />
              als fester Betrag
            </label>
            <p className="feld__hilfe">
              Ein Prozentsatz macht spätere Mengenänderungen mit, ein Betrag steht fest. Beides
              zugleich wären zwei Zahlen für denselben Anspruch.
            </p>
          </fieldset>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anz-wert">
              {nachSatz ? 'Prozentsatz' : 'Betrag'}
            </label>
            <input
              id="anz-wert"
              type="text"
              value={wert}
              onChange={(e) => setzeWert(e.target.value)}
              placeholder={nachSatz ? '30.00' : '500.00'}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anz-faellig">
              Fällig am
            </label>
            <input
              id="anz-faellig"
              type="date"
              value={faellig}
              onChange={(e) => setzeFaellig(e.target.value)}
            />
          </div>

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={bezeichnung === '' || wert === ''}
              onClick={() => {
                beiAnlegen({
                  art,
                  bezeichnung,
                  ...(nachSatz ? { prozentsatz: wert } : { betrag: wert }),
                  faelligAm: faellig,
                });
                setzeBezeichnung('');
              }}
            >
              Hinterlegen
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Abweichungen der erbrachten Leistung (AUF-23).
 *
 * DER TEILSTORNO STEHT IN DER LISTE, ABER NICHT IN DER AUSWAHL. Er entsteht
 * beim Stornieren einer Position (AUF-15), und ein Trigger trägt ihn hier
 * ein. Ihn hier ein zweites Mal erfassen zu können hieße, denselben Vorgang
 * doppelt abzuziehen.
 */
function Abweichungen({
  auftrag,
  bearbeitbar,
  beiErfassen,
}: {
  readonly auftrag: Auftrag;
  readonly bearbeitbar: boolean;
  readonly beiErfassen: (eingabe: Parameters<typeof abweichungErfassen>[1]) => void;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Abweichung[]>([]);
  const [art, setzeArt] = useState<Exclude<Abweichungsart, 'teilstorno'>>('nichterscheinen');
  const [wirkung, setzeWirkung] = useState<Abweichungswirkung>('berechnen');
  const [bemerkung, setzeBemerkung] = useState('');
  const [betrag, setzeBetrag] = useState('');

  useEffect(() => {
    void holeAbweichungen(auftrag.id)
      .then(setzeZeilen)
      .catch(() => setzeZeilen([]));
  }, [auftrag.id, auftrag.stand]);

  const waehlbar: readonly Exclude<Abweichungsart, 'teilstorno'>[] = [
    'nichterscheinen',
    'zusatzgruppe',
    'zusatzleistung_vor_ort',
  ];

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Abweichungen</h2>
        {/* AUF-23 */}
      </div>

      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Art</th>
              <th scope="col">festgestellt</th>
              <th scope="col">Position</th>
              <th scope="col">Bemerkung</th>
              <th scope="col">Wirkung</th>
              <th scope="col" className="zahl">
                Betrag
              </th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z) => (
              <tr key={z.id}>
                <td>{ABWEICHUNGSNAME[z.art]}</td>
                <td className="liste__einzeilig">{alsZeitpunkt(z.festgestelltAm)}</td>
                <td>{z.positionsbezeichnung ?? '—'}</td>
                <td>{z.bemerkung}</td>
                <td>
                  <span
                    className={markenklasse(z.wirkung === 'nicht_berechnen' ? 'stillgelegt' : 'laufend')}
                  >
                    {WIRKUNGSNAME[z.wirkung]}
                  </span>
                  {/* PRD-47 */}
                  {z.erzeugtePosition !== null && (
                    <span className="kanalliste__name"> · eigene Position</span>
                  )}
                </td>
                <td className="zahl">{alsEuro(z.betrag)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {zeilen.length === 0 && (
        <p className="feld__hilfe">Keine Abweichung — die Leistung entspricht dem Auftrag.</p>
      )}

      <p className="feld__hilfe">
        Ein Teilstorno erscheint hier von selbst, sobald eine Position storniert wird.
        Sobald Durchführung und Check-in die übrigen Arten selbsttätig eintragen, entfällt das; bis
        dahin werden sie hier erfasst.
      </p>

      {bearbeitbar && (
        <>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="abw-art">
              Art
            </label>
            <select
              id="abw-art"
              value={art}
              onChange={(e) =>
                setzeArt(e.target.value as Exclude<Abweichungsart, 'teilstorno'>)
              }
            >
              {waehlbar.map((a) => (
                <option key={a} value={a}>
                  {ABWEICHUNGSNAME[a]}
                </option>
              ))}
            </select>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="abw-bemerkung">
              Was ist passiert?
            </label>
            <input
              id="abw-bemerkung"
              type="text"
              value={bemerkung}
              onChange={(e) => setzeBemerkung(e.target.value)}
              placeholder="Zwei Teilnehmer sind nicht erschienen"
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="abw-wirkung">
              Wirkung auf die Fakturierung
            </label>
            <select
              id="abw-wirkung"
              value={wirkung}
              onChange={(e) => setzeWirkung(e.target.value as Abweichungswirkung)}
            >
              {(Object.keys(WIRKUNGSNAME) as Abweichungswirkung[]).map((w) => (
                <option key={w} value={w}>
                  {WIRKUNGSNAME[w]}
                </option>
              ))}
            </select>
            {/* AUF-23 */}
            <p className="feld__hilfe">
              Ohne diese Angabe wäre die Abweichung eine Notiz — sie muss in die Fakturierung
              eingehen.
            </p>
          </div>

          {wirkung !== 'nicht_berechnen' && (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="abw-betrag">
                Betrag
              </label>
              <input
                id="abw-betrag"
                type="text"
                value={betrag}
                onChange={(e) => setzeBetrag(e.target.value)}
                placeholder="400.00"
              />
              <p className="feld__hilfe">
                Was berechnet oder gutgeschrieben werden soll, muss beziffert sein.
              </p>
            </div>
          )}

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={
                bemerkung === '' || (wirkung !== 'nicht_berechnen' && betrag === '')
              }
              onClick={() => {
                beiErfassen({
                  art,
                  festgestelltAm: auftrag.belegdatum,
                  bemerkung,
                  wirkung,
                  ...(betrag === '' ? {} : { betrag }),
                });
                setzeBemerkung('');
                setzeBetrag('');
              }}
            >
              Abweichung erfassen
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Die Änderungshistorie (AUF-18).
 *
 * SIE LÄUFT ÜBER ALLE FASSUNGEN. AUF-18 fragt nach der Historie DES
 * AUFTRAGS, und der besteht aus seinen Fassungen (AUF-14) — eine Historie je
 * Fassung wäre in dem Augenblick nutzlos, in dem sie gebraucht wird.
 *
 * DIE WERTSPALTE HEISST „Positionswert" UND NICHT „Belegsumme". Gerechnet
 * wird Menge mal Einzelpreis; die Belegsumme hängt an Konditionen, Kappung
 * und Steuer je Satzgruppe und ließe sich nur durch vollständiges Nachspielen
 * ermitteln. Der Name sagt es, damit niemand die falsche Zahl liest.
 */
function Historie({ auftrag }: { readonly auftrag: Auftrag }): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Historienzeile[]>([]);

  useEffect(() => {
    void holeHistorie(auftrag.id)
      .then(setzeZeilen)
      .catch(() => setzeZeilen([]));
  }, [auftrag.id, auftrag.stand]);

  return (
    <div className="karte">
      <div className="karte__titel">
        <h2>Änderungshistorie</h2>
        {/* AUF-18 */}
      </div>

      <div className="tafel--weit">
        <table className="liste__tabelle">
          <thead>
            <tr>
              <th scope="col">Wann</th>
              <th scope="col">Wer</th>
              <th scope="col">Was</th>
              <th scope="col">Felder</th>
              <th scope="col" className="zahl">
                Positionswert
              </th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z, i) => (
              <tr key={`${z.zeitpunkt}-${String(i)}`}>
                <td className="liste__einzeilig">{alsZeitpunkt(z.zeitpunkt)}</td>
                <td>{z.benutzer ?? '—'}</td>
                <td>
                  {z.gegenstand}
                  {z.bezeichnung !== null && (
                    <span className="kanalliste__name"> · {z.bezeichnung}</span>
                  )}
                  <span className="marke marke--inaktiv">{z.operation}</span>
                </td>
                <td>{(z.felder ?? []).join(', ') || '—'}</td>
                <td className="zahl">
                  {z.positionswertDifferenz === null ? (
                    '—'
                  ) : (
                    <>
                      {z.positionswertDavor ?? '0.00'} → {z.positionswertDanach ?? '0.00'}
                      <br />
                      <strong>
                        {Number(z.positionswertDifferenz) > 0 ? '+' : ''}
                        {z.positionswertDifferenz}
                      </strong>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {zeilen.length === 0 && <p className="feld__hilfe">Noch keine Änderung protokolliert.</p>}

      <p className="feld__hilfe">
        Die Spalte nennt den <strong>Positionswert</strong> (Menge mal Einzelpreis) und nicht die
        Belegsumme: Die hängt an Konditionen, Kappung und Steuer je Satzgruppe und ließe sich nur
        durch vollständiges Nachspielen des Belegs zu jedem Zeitpunkt ermitteln.
      </p>
    </div>
  );
}

/**
 * DIE BELEGKETTE DES AUFTRAGS (CRM-118, L7; AUF-06, BUC-50).
 *
 * Nach oben Kunde, Angebot und Rahmenauftrag, nach unten Rechnungen samt
 * Gutschrift und Storno, daneben Buchungen und Termine. Entwürfe ohne Nummer
 * stehen mit ihrem Belegdatum da — auch sie sind ein Weg, den man sucht.
 */
function Auftragsverbunden({
  auftrag,
  verbindungen,
  wege,
}: {
  readonly auftrag: Auftrag;
  readonly verbindungen: Auftragsverbindungen;
  readonly wege: Aktenwege | undefined;
}): JSX.Element {
  return (
    <Verbunden
      zeilen={[
        {
          beschriftung: 'Kunde',
          eintraege: [
            <Aktenverweis key="k" ziel="kunde" id={auftrag.kunde} wege={wege}>
              {auftrag.kundenname}
            </Aktenverweis>,
          ],
        },
        {
          beschriftung: 'Angebot',
          eintraege:
            auftrag.angebot === null
              ? []
              : [
                  <Aktenverweis key="a" ziel="angebot" id={auftrag.angebot} wege={wege}>
                    {auftrag.angebotsnummer ?? 'Angebot'}
                  </Aktenverweis>,
                ],
        },
        ...(verbindungen.rahmen === null
          ? []
          : [
              {
                beschriftung: 'Rahmenauftrag',
                eintraege: [
                  <Aktenverweis key="r" ziel="rahmen" id={verbindungen.rahmen.id} wege={wege}>
                    {verbindungen.rahmen.rahmennummer}
                  </Aktenverweis>,
                ],
              },
            ]),
        {
          beschriftung: 'Rechnungen',
          eintraege: verbindungen.rechnungen.map((r) => (
            <Aktenverweis key={r.id} ziel="rechnung" id={r.id} wege={wege}>
              {BELEGARTNAME[r.belegart as Rechnungsart] ?? r.belegart}{' '}
              {r.rechnungsnummer ?? `(Entwurf vom ${alsTag(r.belegdatum) ?? r.belegdatum})`}
            </Aktenverweis>
          )),
        },
        {
          beschriftung: 'Buchungen',
          eintraege: verbindungen.buchungen.map((b) => (
            <Fragment key={b.buchungsnummer}>
              <Aktenverweis ziel="buchung" id={b.buchungsnummer} wege={wege}>
                {b.buchungsnummer}
              </Aktenverweis>
              {b.status === 'gebucht' ? '' : ` · ${b.status}`}
            </Fragment>
          )),
        },
        {
          beschriftung: 'Termine',
          eintraege: verbindungen.termine.map((t) => (
            <Aktenverweis key={t.id} ziel="termin" id={t.id} wege={wege}>
              {alsZeitpunkt(t.beginn) ?? t.beginn}
              {t.bezeichnung === '' ? '' : ` · ${t.bezeichnung}`}
            </Aktenverweis>
          )),
        },
      ]}
    />
  );
}
